/**
 * Scene Editor Store
 *
 * Zustand store with Immer for immutable updates and undo/redo support.
 * Manages the entire scene editing state.
 *
 * The document (spec 004) is `scene` plus the authored `keyframes`; only
 * document changes mark the scene dirty or create undo history. Editor UI
 * state (spec 018: selection, playhead, orientation, viewport, timeline
 * layout) lives in its own fields, is written to IndexedDB 1 s after it
 * changes, and reaches the server only inside a content save or once when
 * leaving the editor.
 */

import { create, useStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import { temporal } from 'zundo';
import type { 
  Story, 
  Scene, 
  Sprite, 
  Dialog, 
  LineExpression,
  Sound, 
  Signal,
  Background,
  SceneApiResponse,
  Transition,
  Orientation,
  ChoiceOption,
  ChoiceSettings,
  EditorState,
  Hinge,
  SpriteSize,
  Keyframe,
  KeyframeTrack,
} from '../../../Shared/src/@types';
import { 
  createDefaultSprite,
  createDefaultDialog,
  createDefaultSound,
  defaultPlaybackSettings,
  generateElementId,
} from '../utils/sceneDefaults';
import { apiClient } from '../api/client';
import { clampSoundTiming, computeRevealTime, fitLineToVoice, lineInMs, soundTypeChange } from '../../../Shared/src/utils/sceneTiming';
import { MAX_CHOICES } from '../../../Shared/src/utils/choices';
import { SCENE_NAME_MAX_LENGTH, normaliseSceneName } from '../../../Shared/src/utils/sceneName';
import { getOperation, playbackToArg, replaySteps, scenePictureMapOf, validateAgainstSchema, type OperationError, type OperationStep, type SceneDocument } from '../../../Shared/src/operations';
import { pictureSlotLabel, validatePictureMap } from '../../../Shared/src/pictures';
import { storedPictureMapFrom } from '../../../Shared/src/storyOperations/document';
// The same place as 039's picture operations see it (a background use by its state).
import { sameUse } from '../../../Shared/src/storyOperations/pictureOperations';
import type { SlotUse, StatePlayback, StoredPictureMap } from '../../../Shared/src/@types';
import { previewDurationMs } from '../utils/sceneDuration';
import {
  getLocalEditorState,
  saveLocalEditorState,
} from '../utils/editorStateDB';
import { interpolateProperty } from '../../../Shared/src/utils/interpolate';
import { resolvePlaybackKeyframes } from '../../../Shared/src/utils/playbackKeyframes';
import { createDefaultBackground as createSharedDefaultBackground, stripRetiredSceneFields } from '../../../Shared/src/utils/sceneContentDefaults';
import {
  BACKGROUND_STATE_NAME_MAX,
  BACKGROUND_STATE_PROPERTY,
  BACKGROUND_TRACK_ID,
  backgroundDefaultState,
  backgroundStateAt,
  backgroundStates,
  renameBackgroundState as renameBackgroundStateOf,
} from '../../../Shared/src/utils/backgroundStates';
import { registerSessionReset } from '../api/session';
import { knownSheets, pictureSizeOf } from './mediaSheetStore';
import { toPlayableScene, type PlayableSceneInput } from '../../../Shared/src/runtime/playableScene';
import type { PlayableScene } from '../../../Shared/src/@types';
import { calculateSceneDuration } from '../utils/sceneDuration';
import {
  getBasisOrientation,
  getResponsivePosition,
  setPositionField,
  setOrientationTransform,
  setAtRestFlip,
} from '../utils/responsive';
import { clampSpriteAxis, compensateHinge, sizeFromImage, stageSize } from '../utils/spriteGeometry';
import { candidateKeyframes } from '../../../Shared/src/utils/interpolate';
import { applyMotionPreset, type MotionPreset } from '../../../Shared/src/utils/motionPresets';
import {
  forkOrientation,
  isUntaggedKeyframe,
  keyframeBaseValue,
  keyframeValueAt,
  putKeyframe,
  writeKeyframe,
} from '../../../Shared/src/utils/keyframeWrite';

/**
 * One canvas gesture, nudge or panel field edit (spec 019): absolute values
 * in the active orientation. Position in % of the stage, rotation in degrees.
 */
export interface SpriteTransformChanges {
  position?: { x?: number; y?: number };
  scale?: number;
  rotation?: number;
}

/** A keyframe on the keyframe clipboard (spec 019), timed relative to the earliest copied one. */
export interface KeyframeClipboardItem {
  trackId: string;
  property: string;
  offset: number;
  value: number | string;
  easing?: Keyframe['easing'];
  /** A background state key's transition (spec 040). */
  transition?: Transition;
}

/** The timeline's zoom step: − divides and + multiplies by this (spec 019). */
export const TIMELINE_ZOOM_STEP = 1.25;
/** Consecutive arrow nudges of one sprite within this window are one undo step (spec 019). */
export const NUDGE_COALESCE_MS = 1000;

/** Base-layout fields one canvas gesture or panel field writes (spec 005). */
export interface SpriteLayoutFields {
  x?: number;
  y?: number;
  scale?: number;
  rotation?: number;
}

/** Why a sprite-state edit was refused (shown inline by the panel). */
export type SpriteStateError = 'empty' | 'duplicate' | 'last' | 'missing';

/** The transition a new background state key starts with (spec 040 D-3). */
export const DEFAULT_BACKGROUND_CHANGE: Transition = { type: 'fade', duration: 500 };

/** Why a background-state edit was refused (spec 040): the sprite shapes, plus a name over 40 characters. */
export type BackgroundStateError = SpriteStateError | 'long';

/** One imported sheet's target (spec 034): a new or an existing state of the sprite. */
export interface SheetImportItem {
  stateName: string;
  create: boolean;
  mediaId: number;
  /** The sheet's natural size; the frame size comes from its cut in `useMediaSheetStore`. */
  naturalSize?: SpriteSize;
}

// Enable Immer's MapSet plugin for using Map/Set in state
enableMapSet();

// =============================================================================
// KEYFRAME WRITE RULES (spec 006): the slot invariant, the orientation fork
// and the value rule live once in Shared/src/utils/keyframeWrite.ts; the
// store's actions wrap them with its own `kf_` ids.
// =============================================================================

let keyframeIdCounter = 0;
/** A new authored keyframe id (`kf_…`). */
export function newKeyframeId(): string {
  keyframeIdCounter = (keyframeIdCounter + 1) % 1_000_000;
  return `kf_${Date.now().toString(36)}_${keyframeIdCounter}_${Math.random().toString(36).slice(2, 6)}`;
}

const isTaggedEdit = (o: Orientation | undefined): o is 'portrait' | 'landscape' =>
  o === 'portrait' || o === 'landscape';

export function keyframesToArray(keyframes: Map<string, Keyframe[]>): KeyframeTrack[] {
  const out: KeyframeTrack[] = [];
  keyframes.forEach((kfs, trackId) => {
    if (kfs.length > 0) out.push({ trackId, keyframes: kfs });
  });
  return out;
}

// =============================================================================
// PLAYBACK SOURCE (spec 006): the authored keyframes plus the derived
// line-expression keys, merged by the precedence rule (resolvePlaybackKeyframes).
// Memoised on the scene, the authored keyframes and the orientation; the
// derived keys are never stored in `keyframes`, never saved, never part of
// undo history.
// =============================================================================

let playbackCache: {
  scene: Story | null;
  keyframes: Map<string, Keyframe[]>;
  byOrientation: Map<Orientation, Map<string, Keyframe[]>>;
} | null = null;

function playbackKeyframeMap(
  scene: Story | null,
  keyframes: Map<string, Keyframe[]>,
  orientation: Orientation,
): Map<string, Keyframe[]> {
  if (!scene) return keyframes;
  if (!playbackCache || playbackCache.scene !== scene || playbackCache.keyframes !== keyframes) {
    playbackCache = { scene, keyframes, byOrientation: new Map() };
  }
  let map = playbackCache.byOrientation.get(orientation);
  if (!map) {
    const tracks = resolvePlaybackKeyframes({ ...scene, keyframes: keyframesToArray(keyframes) }, orientation);
    map = new Map(tracks.map((t) => [t.trackId, t.keyframes]));
    playbackCache.byOrientation.set(orientation, map);
  }
  return map;
}

// =============================================================================
// TYPES
// =============================================================================

type ElementType = 'scene' | 'sprite' | 'dialog' | 'sound' | 'background' | 'signal' | null;

interface Selection {
  ids: string[];
  type: ElementType;
}

// Keyframe types live in Shared (spec 006); re-exported for editor imports.
export type { Keyframe, KeyframeTrack };

/** Where the document stands against the server (spec 004). */
export type SaveStatus = 'saved' | 'dirty' | 'saving' | 'error';

interface SceneState {
  // Data - scene directly contains sprites, dialogs, sounds, etc.
  scene: Story | null;
  isDirty: boolean;
  /** True only while a scene loads; saving never sets it (spec 004). */
  isLoading: boolean;
  /** A load failure; the editor shows it instead of a scene. Never set by a save. */
  error: string | null;
  /** The load failure was a 404. */
  errorNotFound: boolean;
  saveStatus: SaveStatus;
  /** The last save failure's message, while saveStatus is 'error'. */
  saveError: string | null;

  // Editing state
  selection: Selection;
  clipboard: {
    type: ElementType;
    data: unknown[];
  } | null;
  
  /**
   * Authored keyframes only (spec 006), trackId -> keyframes. Line
   * expressions are derived on demand (getPlaybackKeyframes), never stored here.
   */
  keyframes: Map<string, Keyframe[]>;
  
  // Selected keyframe (for focus-aware deletion)
  selectedKeyframe: { trackId: string; keyframeId: string } | null;
  selectedKeyframes: Array<{ trackId: string; keyframeId: string }>;
  /** The keyframe clipboard (spec 019), separate from the element clipboard. UI state. */
  keyframeClipboard: KeyframeClipboardItem[] | null;

  // Playback state (for preview)
  isPlaying: boolean;
  playheadPosition: number;
  /**
   * Preview reached the choice reveal and holds that frame with the panel
   * clickable (spec 008), until an option is clicked, Stop or Escape. While
   * it holds, `isPlaying` stays true and the playhead stays at the reveal.
   */
  choiceHold: boolean;
  /**
   * Where the current ▶ Preview started (spec 009): the playhead, 0 past the
   * play length or from "⏮ From start", the reveal at or past it. Read by the
   * player when the preview starts.
   */
  previewStartMs: number;
  /**
   * A jump of the running preview (spec 009): `seq` bumps on every request,
   * `hold` shows the frame at `ms` paused (a timeline drag), else the
   * preview plays on from `ms`. `null` until the first jump.
   */
  previewJump: { ms: number; seq: number; hold: boolean } | null;

  // UI state
  previewOrientation: Orientation;
  viewportZoom: number;
  viewportPan: { x: number; y: number };
  /** The loaded scene came with a saved viewport (zoom/pan); the canvas then does not fit on open (spec 005). */
  viewportRestored: boolean;
  
  // Panel states (persisted in editorState)
  timelineHeight: number;
  timelineZoom: number;
  timelineScrollLeft: number;
  timelineScrollTop: number;
  expandedTracks: Set<string>;
  collapsedGroups: Set<string>;
  focusedPane: 'canvas' | 'timeline' | 'elements' | 'properties' | null;
  
  // Editor state sync tracking
  editorStateTimestamp: number;  // Last known server timestamp for editor state
}

interface SceneActions {
  // Data operations
  loadScene: (id: number) => Promise<void>;
  /** Save now (Save button, Cmd/Ctrl+S, autosave, leaving). Resolves when this save settles. */
  saveScene: () => Promise<void>;
  createScene: (name: string, folderId?: number | null) => Promise<Story>;

  // Sprite operations
  addSprite: (sprite?: Partial<Sprite>) => void;
  updateSprite: (spriteId: string, updates: Partial<Sprite>) => void;
  deleteSprite: (spriteId: string) => void;
  duplicateSprite: (spriteId: string) => void;
  /**
   * Write base-layout fields for one orientation (spec 005): the basis
   * orientation updates the basis, any other writes field overrides for the
   * fields given. x and y are clamped to −50…150 %. One document change.
   */
  setSpriteLayoutFields: (spriteId: string, orientation: Orientation, fields: SpriteLayoutFields) => void;
  /** Set the sprite's box size (canvas px). One document change. */
  setSpriteSize: (spriteId: string, size: SpriteSize) => void;
  /** Move the hinge (undefined = centre), compensating every explicit position so the box stays put. */
  setSpriteHinge: (spriteId: string, hinge: Hinge | undefined) => void;
  /** Add a state; refuses an empty (after trim) or duplicate name. Returns null on success. */
  addSpriteState: (spriteId: string, name: string) => SpriteStateError | null;
  /** Remove a state; refuses the last one. Deleting the default makes the first remaining the default. */
  removeSpriteState: (spriteId: string, stateName: string) => SpriteStateError | null;
  /** Write `defaultState` only (`currentState` is runtime-only). */
  setDefaultSpriteState: (spriteId: string, stateName: string) => void;
  /**
   * Spec 040: rename a state, with the sprite's `state` keys and the line
   * expressions naming it, in one undo step. Refuses an empty or taken name.
   */
  renameSpriteState: (spriteId: string, from: string, to: string) => SpriteStateError | null;
  /**
   * Give a state its image. A sprite without `size` gets one from the image's
   * natural proportions (`sizeFromImage`); an existing size is kept.
   */
  assignStateMedia: (spriteId: string, stateName: string, mediaId: number, naturalSize?: SpriteSize) => void;
  /**
   * Spec 034: the scene half of a spritesheet import, as one undo step. Adds
   * each `create` state, then points every item's state at its sheet in
   * order (`assignStateMedia`, so a sprite without a size is sized by the
   * first sheet's frame). Refuses, changing nothing, when the sprite is gone,
   * a filled state is missing, or a new name is empty or taken.
   */
  importSheetStates: (spriteId: string, items: SheetImportItem[]) => SpriteStateError | null;
  /**
   * Spec 035: how a state plays its spritesheet, as one undo step (one
   * `editSprite` step setting the state's `playback`; `undefined` removes
   * it). Returns the refusal's reason, or null when applied or unchanged.
   */
  setStatePlayback: (spriteId: string, stateName: string, playback: StatePlayback | undefined) => string | null;
  /**
   * Spec 035: the sheet editor's Done as one undo step: point the state at
   * `mediaId` (when given, `assignStateMedia`) and set its playback (when
   * given; `null` removes it). Returns the refusal's reason, or null.
   */
  applyStateSheet: (
    spriteId: string,
    stateName: string,
    change: { mediaId?: number; naturalSize?: SpriteSize; playback?: StatePlayback | null },
  ) => string | null;

  // Dialog operations
  addDialog: (dialog?: Partial<Dialog>) => void;
  updateDialog: (dialogId: string, updates: Partial<Dialog>) => void;
  /**
   * Sets a line's expression (spec 026): the speaking character and its
   * state while the line plays; `undefined` clears it. One undo step; a
   * value equal to the current one changes nothing.
   */
  setLineExpression: (dialogId: string, lineId: string, value?: LineExpression) => void;
  /**
   * Gives a line its voice (spec 041): sets `audioId` and fits the line to
   * the voice's length with its in-transition (`fitLineToVoice` with
   * `lineInMs`: only when its duration was not set and the length is known).
   * One undo step.
   */
  attachLineVoice: (dialogId: string, lineId: string, mediaId: number, durationMs?: number) => void;
  /** Removes a line's voice; the line keeps its duration (spec 007). One undo step. */
  removeLineVoice: (dialogId: string, lineId: string) => void;
  /** Sets a line's duration in seconds, typed by the author: marks it `durationSet` (spec 041 D-6). One undo step. */
  setLineDuration: (dialogId: string, lineId: string, seconds: number) => void;
  /** "Fit to voice" (spec 041): clears `durationSet` and refits the line to a voice of `durationMs`. One undo step. */
  fitLineToVoiceLength: (dialogId: string, lineId: string, durationMs: number) => void;
  deleteDialog: (dialogId: string) => void;

  // Sound operations
  addSound: (sound: Partial<Sound>) => void;
  updateSound: (soundId: string, updates: Partial<Sound>) => void;
  deleteSound: (soundId: string) => void;
  /**
   * Change a sound's type (spec 007): volume, loop and fades that still hold
   * the old type's default take the new type's; edited ones stay.
   */
  setSoundType: (soundId: string, type: Sound['type']) => void;
  /** Edit start, end and fades, clamped (spec 007 `clampSoundTiming`). */
  setSoundTiming: (soundId: string, patch: Partial<Pick<Sound, 'startTime' | 'endTime' | 'fadeIn' | 'fadeOut'>>) => void;

  // Signal operations (spec 007: no UI creates signals; they are listed and deletable)
  addSignal: (signal: Partial<Signal>) => void;
  updateSignal: (signalId: string, updates: Partial<Signal>) => void;
  deleteSignal: (signalId: string) => void;

  // Background operations
  updateBackground: (updates: Partial<Background>) => void;
  /**
   * Spec 040: the background's states, each one undo step written through
   * 039's `setBackgroundStates` operation (so 039 D-1 and D-2 hold: the
   * current picture becomes the state `default`, `baseMediaId` follows the
   * default state, a removed state loses its keys). Each returns the
   * refusal's reason, or null when applied or unchanged.
   */
  addBackgroundState: (name: string) => BackgroundStateError | null;
  removeBackgroundState: (name: string) => BackgroundStateError | null;
  /** Renames a state and its background keys (039's `renameBackgroundState`) in one step. */
  renameBackgroundState: (from: string, to: string) => BackgroundStateError | null;
  setDefaultBackgroundState: (name: string) => BackgroundStateError | null;
  /** Points a state at a picture (`mediaId` above 0) or clears it (0). */
  assignBackgroundStateMedia: (name: string, mediaId: number) => BackgroundStateError | null;

  // Scene properties operations
  updateSceneProperties: (updates: { name?: string; enterTransition?: Transition; exitTransition?: Transition }) => void;
  /**
   * Rename the scene (header and Scene properties panel both call this).
   * Trimmed, at most SCENE_NAME_MAX_LENGTH characters; an empty or unchanged
   * name is ignored. One document change: dirty, one undo step (spec 018).
   */
  renameScene: (name: string) => void;

  // Undo / redo (spec 018): the document only, one gesture per step
  undo: () => void;
  redo: () => void;
  /** Start a gesture: every document change until the matching end is one undo step. Nests. */
  beginHistoryGroup: () => void;
  /** End a gesture started with beginHistoryGroup. */
  endHistoryGroup: () => void;

  // Selection
  select: (ids: string[], type: ElementType) => void;
  selectSingle: (id: string, type: ElementType) => void;
  addToSelection: (id: string) => void;
  removeFromSelection: (id: string) => void;
  clearSelection: () => void;

  // Clipboard
  copy: () => void;
  paste: () => void;
  cut: () => void;
  deleteSelected: () => void;

  // Playback (spec 009): `play` starts ▶ Preview from the playhead (from 0
  // at or past the play length, held at the reveal at or past it);
  // `playFromStart` starts it from 0, or restarts a running one at 0;
  // `stop` (and `pause`, the timeline's ⏸) ends it and leaves the playhead
  // where the preview was, or parks it at 0 when not previewing.
  play: () => void;
  playFromStart: () => void;
  /**
   * Move the running preview to `ms` (a timeline click or drag): it plays on
   * from there, audio restarted in step; `{ hold: true }` shows that frame
   * paused instead (while dragging). Does nothing while not previewing.
   */
  jumpPreview: (ms: number, options?: { hold?: boolean }) => void;
  pause: () => void;
  stop: () => void;
  setPlayhead: (position: number) => void;
  /** Preview reached the choice reveal: park the playhead there and hold (spec 008). */
  holdAtChoices: (revealMs: number) => void;

  // Keyframe operations (spec 006 write rules: slot invariant, orientation fork)
  /**
   * Add an authored keyframe. It replaces any keyframe in its time + property
   * + orientation slot; a portrait/landscape keyframe first forks the
   * property's untagged keyframes into that orientation. One undo step.
   * Returns the new id.
   */
  addKeyframe: (trackId: string, keyframe: Omit<Keyframe, 'id'>) => string;
  /**
   * Apply an entrance and/or exit preset (spec 016) to a sprite's track
   * through `applyMotionPreset`: every orientation without `orientation`
   * (or with desktop), that orientation's tagged keys with portrait or
   * landscape. One undo step; marks the scene dirty. Returns the keyframes
   * it wrote (empty when nothing changed or the sprite is unknown).
   */
  applySpriteMotionPreset: (spriteId: string, preset: MotionPreset, orientation?: Orientation) => Keyframe[];
  /**
   * Add a keyframe for `property` at the playhead (whole ms) in the active
   * orientation, with the property's current value there (interpolated if
   * animated, else the base value; the default state for `state`), tagged in
   * portrait/landscape and untagged in desktop. Returns the new id, or null.
   */
  addKeyframeAtPlayhead: (trackId: string, property: string) => string | null;
  /** The value a keyframe added now for `property` would take (see addKeyframeAtPlayhead). */
  getKeyframeValueAt: (trackId: string, property: string, time: number, orientation: Orientation) => number | string | undefined;
  /**
   * Edit a keyframe. Moving it onto an occupied slot replaces the occupant.
   * With `orientation` portrait/landscape, editing an untagged keyframe of a
   * property that has no keyframes in that orientation forks first and edits
   * the copy. Returns the id of the keyframe edited (the copy after a fork).
   */
  updateKeyframe: (trackId: string, keyframeId: string, updates: Partial<Keyframe>, orientation?: Orientation) => string | null;
  deleteKeyframe: (trackId: string, keyframeId: string) => void;
  getKeyframesForTrack: (trackId: string) => Keyframe[];
  /**
   * Move keyframes by one delta (spec 019): times round to whole ms and clamp
   * to [0, maxTime]; a moved keyframe landing on an occupied slot replaces
   * the occupant; untagged keyframes moved in portrait/landscape fork first.
   * One undo step. Returns a map from each moved id to the id it now has.
   */
  moveKeyframes: (items: Array<{ trackId: string; keyframeId: string }>, deltaMs: number, maxTime?: number) => Map<string, string>;
  /** Delete keyframes in one undo step; they leave the keyframe selection. */
  deleteKeyframes: (items: Array<{ trackId: string; keyframeId: string }>) => void;
  /** Copy keyframes (default: the selected ones) to the keyframe clipboard, times relative to the earliest. */
  copyKeyframes: (items?: Array<{ trackId: string; keyframeId: string }>) => boolean;
  /**
   * Paste the keyframe clipboard at the playhead, tagged for the active
   * orientation (fork rule applies), with new ids, in one undo step. With a
   * track and a row property ('position', 'opacity', …) the keyframes of
   * that row go onto that track; without, each goes back onto its original
   * track when it still exists. Returns the new keyframes' ids.
   */
  pasteKeyframes: (trackId?: string, rowProperty?: string) => Array<{ trackId: string; keyframeId: string }>;
  /**
   * The one commit for canvas gestures, arrow nudges and the sprite panel's
   * numeric fields (spec 019). A changed property with
   * keyframes driving it in the active orientation gets a keyframe at the
   * playhead and the base layout is left alone; otherwise the base layout is
   * written (spec 005). One undo step.
   */
  commitSpriteTransform: (spriteId: string, changes: SpriteTransformChanges) => void;
  /**
   * Spec 038: the sprite panel's Flip X / Flip Y toggle. With keyframes of
   * the flip counting in the active orientation, a key at the playhead (`1`
   * or `0`, 006's value and tag rules, the orientation fork included);
   * otherwise the at-rest flip, one value for every orientation (D-2). One
   * undo step.
   */
  commitSpriteFlip: (spriteId: string, axis: 'x' | 'y', value: boolean) => void;
  /** Whether `property` of a sprite is driven by authored keyframes in the active orientation. */
  isPropertyAnimated: (spriteId: string, property: string) => boolean;
  /** Nudge a sprite by canvas px in the active orientation via commitSpriteTransform; nudges within 1 s are one undo step. */
  nudgeSprite: (spriteId: string, dxPx: number, dyPx: number) => void;
  /** The keyframes that play in `orientation` (default: the active one), per track: authored plus line expressions (spec 006). Memoised. */
  getPlaybackKeyframes: (orientation?: Orientation) => Map<string, Keyframe[]>;
  /**
   * The scene as the player takes it (spec 013): `toPlayableScene` (012) of
   * the scene with its authored keyframes. Null while no scene is loaded.
   */
  getPlayableScene: () => PlayableScene | null;
  /** `interpolateProperty` over the playing source (getPlaybackKeyframes). Orientation defaults to the active one. */
  getInterpolatedValue: (trackId: string, property: string, time: number, orientation?: Orientation) => number | string | undefined;
  
  // Keyframe selection
  selectKeyframe: (trackId: string, keyframeId: string) => void;
  toggleKeyframeInSelection: (trackId: string, keyframeId: string) => void;
  setKeyframeSelection: (items: Array<{ trackId: string; keyframeId: string }>) => void;
  clearKeyframeSelection: () => void;

  // UI state
  setPreviewOrientation: (orientation: Orientation) => void;
  setViewportZoom: (zoom: number) => void;
  setViewportPan: (pan: { x: number; y: number }) => void;
  
  // Panel state (persisted)
  setTimelineHeight: (height: number) => void;
  setTimelineZoom: (zoom: number) => void;
  /** − / + on the timeline: divide or multiply the zoom by 1.25, clamped to 25 %–400 %. UI state. */
  zoomTimeline: (direction: 'in' | 'out') => void;
  setTimelineScroll: (scrollLeft: number, scrollTop: number) => void;
  setExpandedTracks: (tracks: Set<string>) => void;
  setCollapsedGroups: (groups: Set<string>) => void;
  toggleTrackExpanded: (trackId: string) => void;
  toggleGroupCollapsed: (groupId: string) => void;
  setFocusedPane: (pane: 'canvas' | 'timeline' | 'elements' | 'properties' | null) => void;
  
  // Focus-aware deletion
  handleDelete: () => boolean;  // Returns true if something was deleted

  // Editor UI state (spec 018)
  /** The current editor UI state as a serialisable object (no timestamp). */
  getEditorStateData: () => EditorState;
  /**
   * Leaving the editor: if the UI state changed since it was last written to
   * the server, send it once as `PATCH {editorState}`. The only standalone
   * editor-state request.
   */
  syncEditorStateToServer: () => Promise<void>;

  // Choice operations
  addChoice: () => void;
  updateChoice: (id: string, updates: Partial<ChoiceOption>) => void;
  removeChoice: (id: string) => void;
  /**
   * Move a choice to `toIndex` (clamped) as one undo step (spec 025); ids, and
   * so the story's connections, are unchanged. A move to where it already is,
   * or past either end, does nothing and records nothing.
   */
  moveChoice: (id: string, toIndex: number) => void;
  /** Remove every option as one action and one undo step; `choices` becomes `[]` (spec 008). */
  clearChoices: () => void;
  setChoiceSettings: (settings: Partial<ChoiceSettings>) => void;

  // Utility
  /**
   * Replay recorded scene operations (spec 021) on the document: all or
   * nothing, one undo step, dirty and autosaved like any edit. Each step's
   * `newId()` returns its recorded `createdIds`. Selection is kept, minus
   * ids of elements the batch removed.
   *
   * `naturalSizes` (media id → the image's natural size, spec 022) becomes
   * the replay context's `imageSizes` (spec 021 D-19): a sprite a step
   * creates or re-pictures without a stored `size` takes its box from its
   * picture by the operations' own rule, exactly as on the Service.
   *
   * The steps replay against the scene with `keyframes:
   * keyframesToArray(keyframes)`, and the result's keyframes are written
   * back into the track map in the same update as the scene (one undo step
   * over both; a keyframe-only batch dirties the scene).
   */
  applyOperations: (steps: OperationStep[], options?: ApplyOperationsOptions) => ApplyOperationsResult;

  /**
   * Spec 030: fills a slot of the scene's own picture map with a library
   * image: loads the image's natural size, applies one `fillPictureSlot`
   * step (one undo step; every sprite of that name and expression, or only
   * `uses`), then saves the scene at once (D-5).
   */
  fillPicture: (slotId: string, mediaId: number, opts?: { uses?: SlotUse[] }) => Promise<SceneFillPictureResult>;
  /** Spec 030: discards pictures from the scene (one `clearPictures` step, one undo step), then saves. */
  discardPictures: (mediaIds: number[]) => Promise<ScenePictureApplyResult>;

  markDirty: () => void;
  markClean: () => void;
}

/** Extra inputs to `applyOperations` (spec 022). */
export interface ApplyOperationsOptions {
  naturalSizes?: ReadonlyMap<number, SpriteSize>;
  /** Lengths (ms) of the voices the steps name (spec 041): `setDialog` fits a voiced line by them, as on the Service. */
  mediaLengths?: ReadonlyMap<number, number>;
}

/** What `applyOperations` returns. */
export type ApplyOperationsResult =
  | { ok: true }
  | { ok: false; failedIndex: number; errors: OperationError[] };

/** What a scene picture change resolves to (spec 030): the story store's shape. */
export type ScenePictureApplyResult =
  | { ok: true }
  | { ok: false; reason: 'failed' | 'network'; errors: OperationError[] };

/** What `fillPicture` resolves to (spec 030): on success, the places the picture went into. */
export type SceneFillPictureResult =
  | { ok: true; places: number; label: string }
  | { ok: false; reason: 'failed' | 'network'; errors: OperationError[] };


/**
 * The scene's own picture map as a save sends it (spec 030 D-2): the stored
 * slots, each one the Service's `validatePictureMap` takes (a slot it would
 * refuse, such as one named only by punctuation, is left out so the save
 * never fails over it).
 */
export function savedPictureMapOf(map: StoredPictureMap | undefined | null): StoredPictureMap {
  return { slots: storedPictureMapFrom(map).slots.filter((slot) => validatePictureMap({ slots: [slot] }).ok) };
}

type SceneStore = SceneState & SceneActions;

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: SceneState = {
  scene: null,
  isDirty: false,
  isLoading: false,
  error: null,
  errorNotFound: false,
  saveStatus: 'saved',
  saveError: null,
  selection: { ids: ['scene'], type: 'scene' }, // Default to scene selection
  clipboard: null,
  keyframes: new Map(),
  selectedKeyframe: null,
  selectedKeyframes: [],
  keyframeClipboard: null,
  isPlaying: false,
  playheadPosition: 0,
  choiceHold: false,
  previewStartMs: 0,
  previewJump: null,
  previewOrientation: 'desktop',
  viewportZoom: 1,
  viewportPan: { x: 0, y: 0 },
  viewportRestored: false,
  // Panel states with sensible defaults
  timelineHeight: 200,
  timelineZoom: 1,
  timelineScrollLeft: 0,
  timelineScrollTop: 0,
  expandedTracks: new Set(),
  collapsedGroups: new Set(),
  focusedPane: null,
  // Editor state sync
  editorStateTimestamp: 0,
};

// =============================================================================
// DRAFT CACHE — preserves unsaved scene state across tab switches
// =============================================================================

interface DraftSnapshot {
  /** The live store objects the draft was taken from, to skip re-caching an unchanged scene. */
  sourceScene: Story;
  sourceKeyframes: Map<string, Keyframe[]>;
  scene: Story;
  keyframes: Map<string, Keyframe[]>;
  selection: Selection;
  selectedKeyframe: { trackId: string; keyframeId: string } | null;
  playheadPosition: number;
  previewOrientation: Orientation;
  viewportZoom: number;
  viewportPan: { x: number; y: number };
  timelineHeight: number;
  timelineZoom: number;
  timelineScrollLeft: number;
  timelineScrollTop: number;
  expandedTracks: Set<string>;
  collapsedGroups: Set<string>;
  focusedPane: 'canvas' | 'timeline' | 'elements' | 'properties' | null;
}

const _draftCache = new Map<number, DraftSnapshot>();

// =============================================================================
// DEBOUNCE UTILITIES
// =============================================================================

/** UI state reaches IndexedDB this long after its last change (spec 018). */
export const EDITOR_STATE_LOCAL_DEBOUNCE_MS = 1000;
/** The longest scene name the header and the properties panel accept. */
export { SCENE_NAME_MAX_LENGTH };
/** Undo history depth. */
export const UNDO_LIMIT = 100;

let localSaveTimeout: ReturnType<typeof setTimeout> | null = null;

function debouncedLocalSave(sceneId: number, state: EditorState) {
  if (localSaveTimeout) clearTimeout(localSaveTimeout);
  localSaveTimeout = setTimeout(() => {
    writeLocalEditorStateSafely(sceneId, state);
    localSaveTimeout = null;
  }, EDITOR_STATE_LOCAL_DEBOUNCE_MS);
}

/**
 * The editor UI state last written to (or loaded from) the server, serialised,
 * for the loaded scene; null when the server copy is known to be behind.
 * Leaving the editor sends the UI state only when it differs from this.
 */
let serverEditorStateJson: string | null = null;

const serialiseEditorState = (state: EditorState) => JSON.stringify(state);

/** Timeline rows and the keyframe properties each carries (spec 019). */
export const TIMELINE_ROW_PROPERTIES: Record<string, string[]> = {
  position: ['position.x', 'position.y'],
  hinge: ['hinge.x', 'hinge.y'],
  'transform.scale': ['transform.scale'],
  'transform.rotation': ['transform.rotation'],
  'transform.flipX': ['transform.flipX'],
  'transform.flipY': ['transform.flipY'],
  opacity: ['opacity'],
  state: ['state'],
};

/** The last arrow nudge, for coalescing a run of nudges into one undo step. */
let lastNudge: { spriteId: string; at: number; scene: Story | null; keyframes: Map<string, Keyframe[]> } | null = null;

// =============================================================================
// UNDO HISTORY (spec 018)
// History covers the document (`scene` + authored `keyframes`) and nothing
// else. A gesture is one step: beginHistoryGroup() pauses zundo and remembers
// the document; endHistoryGroup() resumes it and records that one step.
// Line-expression keys are computed (getPlaybackKeyframes), never stored,
// so they never reach history.
// =============================================================================

interface HistorySnapshot {
  scene: Story | null;
  keyframes: Map<string, Keyframe[]>;
}

let historyGroupDepth = 0;
let historyGroupStart: HistorySnapshot | null = null;

const historySnapshot = (): HistorySnapshot => {
  const { scene, keyframes } = useSceneStore.getState();
  return { scene, keyframes };
};

/** Immer shares untouched structure, so a document change is a new reference. */
const sameDocument = (a: HistorySnapshot, b: HistorySnapshot) =>
  a.scene === b.scene && a.keyframes === b.keyframes;

function beginHistoryGroup() {
  if (historyGroupDepth++ > 0) return;
  historyGroupStart = historySnapshot();
  useSceneStore.temporal.getState().pause();
}

function endHistoryGroup() {
  if (historyGroupDepth === 0) return;
  if (--historyGroupDepth > 0) return;
  const start = historyGroupStart;
  historyGroupStart = null;
  const temporalStore = useSceneStore.temporal;
  temporalStore.getState().resume();
  if (!start || !start.scene || sameDocument(start, historySnapshot())) return;
  temporalStore.setState({
    pastStates: [...temporalStore.getState().pastStates, start].slice(-UNDO_LIMIT),
    futureStates: [],
  });
}

/** Drop any open group and the history (load, sign-out). */
function resetHistory() {
  historyGroupDepth = 0;
  historyGroupStart = null;
  const t = useSceneStore.temporal.getState();
  t.clear();
  t.resume();
}

/** Run a change that must never become an undo step of its own. */
function withoutHistory(fn: () => void) {
  const t = useSceneStore.temporal.getState();
  const wasTracking = t.isTracking;
  if (wasTracking) t.pause();
  try {
    fn();
  } finally {
    if (wasTracking) useSceneStore.temporal.getState().resume();
  }
}


/** Run a document change made of several store updates as one undo step. */
function asOneStep(fn: () => void) {
  beginHistoryGroup();
  try {
    fn();
  } finally {
    endHistoryGroup();
  }
}

/** States as `setBackgroundStates` takes them: a picture only when set. */
const stateArgs = (states: readonly { name: string; mediaId?: number }[]) =>
  states.map((s) => (typeof s.mediaId === 'number' && s.mediaId > 0 ? { name: s.name, mediaId: s.mediaId } : { name: s.name }));

/** One `setBackgroundStates` step (spec 040), through `applyOperations`: one undo step. */
function writeBackgroundStatesStep(
  get: () => SceneStore,
  states: { name: string; mediaId?: number }[],
  defaultState: string | undefined,
): BackgroundStateError | null {
  const args = { states, ...(defaultState !== undefined ? { defaultState } : {}) };
  const result = get().applyOperations([{ name: 'setBackgroundStates', args, createdIds: [] }]);
  if (result.ok) return null;
  return result.errors.some((e) => /two states/.test(e.message)) ? 'duplicate'
    : result.errors.some((e) => /at most/.test(e.message)) ? 'long'
      : result.errors.some((e) => /empty/.test(e.message)) ? 'empty' : 'missing';
}

/** The selection with ids that no longer exist removed; empty falls back to the scene. */
function pruneSelection(scene: Story | null, selection: Selection): Selection {
  if (!scene || selection.type === 'scene' || selection.type === null) {
    return { ids: ['scene'], type: 'scene' };
  }
  const exists = (id: string) => {
    switch (selection.type) {
      case 'sprite': return scene.sprites.some((s) => s.id === id);
      case 'dialog': return scene.dialogs.some((d) => d.id === id);
      case 'sound': return scene.sounds.some((s) => s.id === id);
      case 'signal': return scene.signals.some((s) => s.id === id);
      case 'background': return true;
      default: return false;
    }
  };
  const ids = selection.ids.filter(exists);
  if (ids.length === 0) return { ids: ['scene'], type: 'scene' };
  return ids.length === selection.ids.length ? selection : { ids, type: selection.type };
}

// =============================================================================
// PERSISTENCE (spec 004): revision counter, single in-flight save, autosave
// =============================================================================

/** Autosave fires this long after the last document change… */
export const AUTOSAVE_DEBOUNCE_MS = 2000;
/** …and no later than this after the first unsaved change while edits keep coming. */
export const AUTOSAVE_MAX_WAIT_MS = 10000;
/** A failed save is retried by autosave after this long. */
export const AUTOSAVE_RETRY_MS = 10000;

/** Increments on every document change of the loaded scene; reset on load. */
let docRevision = 0;
/** Increments on every loadScene call; a late response for an older call is discarded. */
let loadGeneration = 0;

let autosaveDebounce: ReturnType<typeof setTimeout> | null = null;
let autosaveMaxWait: ReturnType<typeof setTimeout> | null = null;

interface SaveJob {
  sceneId: number;
  generation: number;
  revision: number;
  payload: Partial<SceneApiResponse> & Record<string, unknown>;
  /** The draft cached for this scene when the job was built; cleared on success if still the same. */
  draft: DraftSnapshot | undefined;
  /** The editor UI state this save carries, serialised (spec 018). */
  editorStateJson: string;
}

let saveInFlight: Promise<void> | null = null;
let queuedJob: SaveJob | null = null;
let queuedPromise: Promise<void> | null = null;

function clearAutosaveTimers() {
  if (autosaveDebounce) clearTimeout(autosaveDebounce);
  if (autosaveMaxWait) clearTimeout(autosaveMaxWait);
  autosaveDebounce = null;
  autosaveMaxWait = null;
}

function fireAutosave() {
  clearAutosaveTimers();
  const s = useSceneStore.getState();
  if (!s.scene || !s.isDirty) return;
  // A save is running: when it finishes it schedules autosave again if still dirty.
  if (saveInFlight) return;
  void s.saveScene();
}

/** Schedule autosave after a document change (debounce, bounded by the max wait). */
function scheduleAutosave(delay = AUTOSAVE_DEBOUNCE_MS) {
  if (autosaveDebounce) clearTimeout(autosaveDebounce);
  autosaveDebounce = setTimeout(fireAutosave, delay);
  if (!autosaveMaxWait) autosaveMaxWait = setTimeout(fireAutosave, AUTOSAVE_MAX_WAIT_MS);
}

/** IndexedDB may be unavailable or throw (private mode, quota): never let it block a load. */
async function readLocalEditorStateSafely(sceneId: number) {
  try {
    return await getLocalEditorState(sceneId);
  } catch (err) {
    console.warn('[EditorState] IndexedDB read failed; using the server state', err);
    return null;
  }
}

/** An IndexedDB write that fails is dropped: it never surfaces or blocks a save. */
function writeLocalEditorStateSafely(sceneId: number, state: EditorState) {
  Promise.resolve()
    .then(() => saveLocalEditorState(sceneId, state))
    .catch((err) => console.warn('[EditorState] IndexedDB write failed', err));
}

/**
 * The PATCH body for a save: every document field plus the editor UI state.
 * Optional document fields go as null when absent so clearing one persists
 * (the Service merges provided keys over the stored content).
 */
function buildSavePayload(s: SceneState & SceneActions): SaveJob['payload'] {
  const scene = s.scene!;
  return {
    name: scene.name,
    iconId: scene.iconId,
    background: scene.background,
    sprites: scene.sprites,
    dialogs: scene.dialogs,
    sounds: scene.sounds,
    signals: scene.signals,
    keyframes: keyframesToArray(s.keyframes),
    playbackSettings: scene.playbackSettings,
    choices: scene.choices ?? null,
    choiceSettings: scene.choiceSettings ?? null,
    duration: scene.duration,
    enterTransition: scene.enterTransition ?? null,
    exitTransition: scene.exitTransition ?? null,
    // Spec 030 D-2: the scene's own map, a top-level key the Service writes to its column.
    ...(scene.pictureMap ? { pictureMap: savedPictureMapOf(scene.pictureMap) } : {}),
    editorState: { ...s.getEditorStateData(), timestamp: Date.now() },
  } as SaveJob['payload'];
}

function buildSaveJob(): SaveJob {
  const s = useSceneStore.getState();
  return {
    sceneId: s.scene!.id,
    generation: loadGeneration,
    revision: docRevision,
    payload: buildSavePayload(s),
    draft: _draftCache.get(s.scene!.id),
    editorStateJson: serialiseEditorState(s.getEditorStateData()),
  };
}

/** True when the job's scene is still the one loaded (same loadScene call). */
const isCurrent = (job: SaveJob) =>
  job.generation === loadGeneration && useSceneStore.getState().scene?.id === job.sceneId;

async function runSaveJob(job: SaveJob): Promise<void> {
  if (isCurrent(job)) {
    useSceneStore.setState({ saveStatus: 'saving', saveError: null });
  }
  let failure: string | null = null;
  try {
    const response = await apiClient.updateScene(job.sceneId, job.payload as Partial<SceneApiResponse>);
    if (!response?.success) failure = response?.error || 'Failed to save scene';
  } catch (err) {
    failure = err instanceof Error ? err.message : 'Failed to save scene';
  }

  if (!failure && _draftCache.get(job.sceneId) === job.draft) {
    // The server now holds what the draft held.
    _draftCache.delete(job.sceneId);
  }
  if (!isCurrent(job)) return;

  const more = queuedJob !== null;
  if (!failure) {
    // The server now holds the UI state this save carried.
    serverEditorStateJson = job.editorStateJson;
    const ts = (job.payload.editorState as EditorState | undefined)?.timestamp;
    if (ts) useSceneStore.setState({ editorStateTimestamp: ts });
  }
  if (failure) {
    useSceneStore.setState({ saveStatus: 'error', saveError: failure });
    if (!more) scheduleAutosave(AUTOSAVE_RETRY_MS);
    return;
  }
  if (docRevision === job.revision) {
    useSceneStore.setState({ isDirty: false, saveStatus: more ? 'saving' : 'saved', saveError: null });
  } else {
    // Edited while the request was out: stay dirty, keep the edits, save again.
    useSceneStore.setState({ saveStatus: more ? 'saving' : 'dirty', saveError: null });
    if (!more) scheduleAutosave();
  }
}

function startSaveJob(job: SaveJob): Promise<void> {
  const p: Promise<void> = runSaveJob(job).finally(() => {
    if (saveInFlight === p) saveInFlight = null;
  });
  saveInFlight = p;
  return p;
}

/**
 * At most one content save is in flight. A save requested meanwhile is queued
 * (a newer request replaces the queued snapshot) and runs once after it.
 */
function enqueueSave(job: SaveJob): Promise<void> {
  if (!saveInFlight) return startSaveJob(job);
  queuedJob = job;
  if (!queuedPromise) {
    queuedPromise = saveInFlight.then(() => {
      const next = queuedJob!;
      queuedJob = null;
      queuedPromise = null;
      return enqueueSave(next);
    });
  }
  return queuedPromise;
}

// =============================================================================
// UI STATE RESTORE (spec 018)
// =============================================================================

export const VIEWPORT_ZOOM_RANGE = [0.1, 3] as const;
export const TIMELINE_ZOOM_RANGE = [0.25, 4] as const;
export const TIMELINE_MIN_HEIGHT = 100;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const timelineMaxHeight = () =>
  Math.max(TIMELINE_MIN_HEIGHT, (typeof window !== 'undefined' ? window.innerHeight : 1000) * 0.6);
const ORIENTATIONS: readonly Orientation[] = ['portrait', 'landscape', 'desktop'];
const PANES = ['canvas', 'timeline', 'elements', 'properties'] as const;

/**
 * Where ▶ Preview starts for a playhead (spec 009): the playhead itself; 0 at
 * or past the play length (`previewDurationMs`), so ▶ never opens on a
 * finished scene; the choice reveal at or past it (held with the panel).
 */
export function previewStartFor(
  scene: SceneState['scene'],
  keyframes: Map<string, Keyframe[]>,
  playheadMs: number,
): number {
  if (!scene) return 0;
  const t = Number.isFinite(playheadMs) ? Math.max(0, playheadMs) : 0;
  const length = previewDurationMs(scene, keyframes);
  if (t >= length) return 0;
  if ((scene.choices?.length ?? 0) > 0) {
    const reveal = computeRevealTime(length, scene.choiceSettings?.showBeforeEnd, scene.exitTransition);
    if (t >= reveal) return reveal;
  }
  return t;
}

type RestoredUiState = Pick<SceneState,
  'selection' | 'selectedKeyframe' | 'selectedKeyframes' | 'playheadPosition' | 'previewOrientation' |
  'viewportZoom' | 'viewportPan' | 'timelineHeight' | 'timelineZoom' | 'timelineScrollLeft' |
  'timelineScrollTop' | 'expandedTracks' | 'collapsedGroups' | 'focusedPane'>;

/**
 * Validate a stored UI state against the scene it belongs to: a selected
 * element or keyframe that no longer exists falls back to the scene / no
 * keyframe, the playhead is clamped to [0, scene duration] and zooms to their
 * ranges. Missing or malformed fields take their defaults.
 */
export function restoreEditorState(
  stored: Partial<EditorState> | null | undefined,
  scene: Story,
  keyframes: Map<string, Keyframe[]>,
): RestoredUiState {
  const e = stored ?? {};
  let selection: Selection = { ids: ['scene'], type: 'scene' };
  if (e.selectedElementId && e.selectedElementType) {
    const candidate = pruneSelection(scene, { ids: [e.selectedElementId], type: e.selectedElementType });
    if (candidate.type !== 'scene') selection = candidate;
  }

  let selectedKeyframe: RestoredUiState['selectedKeyframe'] = null;
  const kf = e.selectedKeyframe;
  if (kf?.trackId && kf.keyframeId && keyframes.get(kf.trackId)?.some((k) => k.id === kf.keyframeId)) {
    selectedKeyframe = { trackId: kf.trackId, keyframeId: kf.keyframeId };
  }

  const duration = Math.max(calculateSceneDuration(scene), isNum(scene.duration) ? scene.duration : 0);
  const pan = e.viewportPan;

  return {
    selection,
    selectedKeyframe,
    selectedKeyframes: selectedKeyframe ? [selectedKeyframe] : [],
    playheadPosition: isNum(e.playheadPosition) ? clamp(e.playheadPosition, 0, duration) : 0,
    previewOrientation: e.previewOrientation && ORIENTATIONS.includes(e.previewOrientation)
      ? e.previewOrientation
      : initialState.previewOrientation,
    viewportZoom: isNum(e.viewportZoom) ? clamp(e.viewportZoom, ...VIEWPORT_ZOOM_RANGE) : initialState.viewportZoom,
    viewportPan: pan && isNum(pan.x) && isNum(pan.y) ? { x: pan.x, y: pan.y } : { ...initialState.viewportPan },
    timelineHeight: isNum(e.timelineHeight)
      ? clamp(e.timelineHeight, TIMELINE_MIN_HEIGHT, timelineMaxHeight())
      : initialState.timelineHeight,
    timelineZoom: isNum(e.timelineZoom) ? clamp(e.timelineZoom, ...TIMELINE_ZOOM_RANGE) : initialState.timelineZoom,
    timelineScrollLeft: isNum(e.timelineScrollLeft) ? Math.max(0, e.timelineScrollLeft) : 0,
    timelineScrollTop: isNum(e.timelineScrollTop) ? Math.max(0, e.timelineScrollTop) : 0,
    // Without a stored choice, the tracks that have keyframes open, so a scene's
    // animation shows on the timeline without a click (spec 006 AC-35).
    expandedTracks: new Set(Array.isArray(e.expandedTracks)
      ? e.expandedTracks
      : Array.from(keyframes).filter(([, kfs]) => kfs.length > 0).map(([trackId]) => trackId)),
    collapsedGroups: new Set(Array.isArray(e.collapsedGroups) ? e.collapsedGroups : []),
    focusedPane: e.focusedPane && (PANES as readonly string[]).includes(e.focusedPane) ? e.focusedPane : null,
  };
}

/** A copy's layout: +5 % on both axes in the given orientation (spec 005). */
function offsetLayout(layout: Sprite['layout'], orientation: Orientation): Sprite['layout'] {
  const pos = getResponsivePosition(layout, orientation);
  const withX = setPositionField(layout, orientation, 'x', clampSpriteAxis(pos.x + 5));
  return setPositionField(withX, orientation, 'y', clampSpriteAxis(pos.y + 5));
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

// =============================================================================
// STORE
// =============================================================================

export const useSceneStore = create<SceneStore>()(
  subscribeWithSelector(
    temporal(
      immer((set, get) => ({
      ...initialState,

      // =========================================================================
      // DATA OPERATIONS
      // =========================================================================

      loadScene: async (id: number) => {
        // A dirty scene still in the store is never dropped: cache it and save it.
        const before = get();
        if (before.scene && before.isDirty && cacheDraft()) {
          void before.saveScene();
        } else if (before.scene && !before.isDirty && before.scene.id !== id) {
          // Leaving a clean scene for another one: its UI state goes once (spec 018).
          void before.syncEditorStateToServer();
        }

        // The last call wins: a late response for an earlier call is discarded.
        const generation = ++loadGeneration;
        clearAutosaveTimers();
        docRevision = 0;
        serverEditorStateJson = null;
        if (localSaveTimeout) clearTimeout(localSaveTimeout);
        localSaveTimeout = null;
        // Undo history is per scene and starts empty on every load (spec 018);
        // nothing the load itself does is recorded.
        resetHistory();
        useSceneStore.temporal.getState().pause();

        // Reset per-scene state at once; the clipboard is kept for cross-scene paste.
        set((state) => {
          state.scene = null;
          state.selection = { ids: ['scene'], type: 'scene' };
          state.keyframes = new Map();
          state.selectedKeyframe = null;
          state.selectedKeyframes = [];
          state.isPlaying = false;
          state.choiceHold = false;
          state.previewStartMs = 0;
          state.playheadPosition = 0;
          state.isDirty = false;
          state.isLoading = true;
          state.error = null;
          state.errorNotFound = false;
          state.saveStatus = 'saved';
          state.saveError = null;
        });

        /** Loading is over for this call: start recording from an empty history. */
        const finishLoad = () => {
          if (generation !== loadGeneration) return;
          resetHistory();
        };

        // ── Restore from draft cache if available (unsaved tab switch) ──
        const draft = _draftCache.get(id);
        if (draft) {
          set((state) => {
            state.scene = draft.scene;
            state.keyframes = draft.keyframes;
            state.selection = draft.selection;
            state.selectedKeyframe = draft.selectedKeyframe;
            state.selectedKeyframes = draft.selectedKeyframe ? [draft.selectedKeyframe] : [];
            state.playheadPosition = draft.playheadPosition;
            state.previewOrientation = draft.previewOrientation;
            state.viewportZoom = draft.viewportZoom;
            state.viewportPan = draft.viewportPan;
            state.viewportRestored = true;
            state.timelineHeight = draft.timelineHeight;
            state.timelineZoom = draft.timelineZoom;
            state.timelineScrollLeft = draft.timelineScrollLeft;
            state.timelineScrollTop = draft.timelineScrollTop;
            state.expandedTracks = draft.expandedTracks;
            state.collapsedGroups = draft.collapsedGroups;
            state.focusedPane = draft.focusedPane;
            state.isDirty = true;
            state.saveStatus = 'dirty';
            state.isLoading = false;
            state.error = null;
          });
          finishLoad();
          scheduleAutosave();
          return;
        }

        try {
          // Fetch both server and local state in parallel; IndexedDB may fail.
          const [response, localState] = await Promise.all([
            apiClient.getScene(id),
            readLocalEditorStateSafely(id),
          ]);
          if (generation !== loadGeneration) return;

          if (!response.success) {
            const notFound = response.status === 404 || /not found/i.test(response.error || '');
            throw Object.assign(new Error(response.error || 'Failed to load scene'), { notFound });
          }
          // API returns normalized data with content expanded. What Storyboard
          // left (spec 006 v5) never reaches the store; every keyframe is kept.
          const data = stripRetiredSceneFields(response.data);
          
          const scene: Story = {
            id: data.id,
            name: data.name,
            userId: data.userId,
            folderId: data.folderId,
            iconId: data.iconId,
            background: data.background || createSharedDefaultBackground(),
            sprites: data.sprites || [],
            dialogs: data.dialogs || [],
            sounds: data.sounds || [],
            signals: data.signals || [],
            playbackSettings: data.playbackSettings || defaultPlaybackSettings,
            choices: data.choices || undefined,
            choiceSettings: data.choiceSettings || undefined,
            duration: data.duration ?? undefined,
            enterTransition: data.enterTransition ?? undefined,
            exitTransition: data.exitTransition ?? undefined,
            // The scene's own picture map (spec 030 D-2), when the Service sent one.
            ...(data.pictureMap ? { pictureMap: data.pictureMap } : {}),
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          };

          // Every saved keyframe is kept (spec 006 v5: nothing is filtered out).
          const keyframes = new Map<string, Keyframe[]>();
          const savedKeyframes = data.keyframes;
          if (savedKeyframes && Array.isArray(savedKeyframes)) {
            savedKeyframes.forEach(({ trackId, keyframes: saved }) => {
              const kfs = saved ?? [];
              if (kfs.length > 0) keyframes.set(trackId, kfs);
            });
          }

          // UI state: the newer of the IndexedDB record and the server copy wins.
          const serverState = data.editorState ?? null;
          const serverTimestamp = serverState?.timestamp || 0;
          const localTimestamp = localState?.timestamp || 0;
          const useLocalState = Boolean(localState) && localTimestamp > serverTimestamp;
          const restored = restoreEditorState(
            useLocalState ? localState!.state : serverState,
            scene,
            keyframes,
          );

          set((state) => {
            state.scene = scene;
            state.keyframes = keyframes;
            Object.assign(state, restored);
            state.viewportRestored = isNum((useLocalState ? localState!.state : serverState)?.viewportZoom);
            state.isLoading = false;
            state.isDirty = false;
            state.editorStateTimestamp = useLocalState ? localTimestamp : serverTimestamp;
          });

          // The server holds its own copy; when IndexedDB was newer it is behind,
          // and leaving the editor sends the UI state once. The load sends nothing.
          serverEditorStateJson = useLocalState ? null : serialiseEditorState(get().getEditorStateData());
          finishLoad();
        } catch (error) {
          if (generation !== loadGeneration) return;
          set((state) => {
            state.scene = null;
            state.isLoading = false;
            state.error = error instanceof Error ? error.message : 'Failed to load scene';
            state.errorNotFound = Boolean((error as { notFound?: boolean } | null)?.notFound);
          });
          finishLoad();
        }
      },

      saveScene: () => {
        const { scene, isDirty } = get();
        if (!scene || !isDirty) {
          // Nothing to send; a caller that must wait for a running save still can.
          return queuedPromise ?? saveInFlight ?? Promise.resolve();
        }
        clearAutosaveTimers();
        // Snapshot now: edits made while the request is out are not part of it.
        return enqueueSave(buildSaveJob());
      },

      createScene: async (name: string, folderId?: number | null) => {
        const response = await apiClient.createScene({ name, folderId: folderId ?? undefined });
        if (!response.success) {
          throw new Error(response.error || 'Failed to create scene');
        }
        // API returns SceneApiResponse, convert to internal Story type
        const data = response.data;
        const story: Story = {
          id: data.id,
          name: data.name,
          userId: data.userId,
          folderId: data.folderId,
          iconId: data.iconId,
          background: data.background || createSharedDefaultBackground(),
          sprites: data.sprites || [],
          dialogs: data.dialogs || [],
          sounds: data.sounds || [],
          signals: data.signals || [],
          playbackSettings: data.playbackSettings || defaultPlaybackSettings,
          duration: data.duration ?? undefined,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        };
        return story;
      },

      // =========================================================================
      // SPRITE OPERATIONS
      // =========================================================================

      addSprite: (spriteData?: Partial<Sprite>) => {
        const orientation = get().previewOrientation;
        const sprite = createDefaultSprite(undefined, undefined, orientation);
        if (spriteData) {
          Object.assign(sprite, spriteData);
        }

        set((state) => {
          if (!state.scene) return;
          state.scene.sprites.push(sprite);
          state.selection = { ids: [sprite.id], type: 'sprite' };
          state.isDirty = true;
        });
      },

      updateSprite: (spriteId: string, updates: Partial<Sprite>) => {
        set((state) => {
          if (!state.scene) return;
          const sprite = state.scene.sprites.find((s) => s.id === spriteId);
          if (!sprite) return;
          Object.assign(sprite, updates);
          state.isDirty = true;
        });
      },

      deleteSprite: (spriteId: string) => {
        set((state) => {
          if (!state.scene) return;
          const index = state.scene.sprites.findIndex((s) => s.id === spriteId);
          if (index === -1) return;
          state.scene.sprites.splice(index, 1);
          // The sprite's keyframe track goes in the same update: one undo step restores both (spec 005).
          state.keyframes.delete(spriteId);

          if (state.selection.ids.includes(spriteId)) {
            state.selection.ids = state.selection.ids.filter((id) => id !== spriteId);
            if (state.selection.ids.length === 0) {
              // Default to scene selection
              state.selection = { ids: ['scene'], type: 'scene' };
            }
          }
          state.isDirty = true;
        });
      },

      duplicateSprite: (spriteId: string) => {
        set((state) => {
          if (!state.scene) return;
          const sprite = state.scene.sprites.find((s) => s.id === spriteId);
          if (!sprite) return;

          const orientation = state.previewOrientation;
          const duplicate = createDefaultSprite(`${sprite.name} (Copy)`, undefined, orientation);
          
          // Deep copy the sprite
          Object.assign(duplicate, JSON.parse(JSON.stringify(sprite)), {
            id: duplicate.id,
            name: `${sprite.name} (Copy)`,
          });
          
          // Offset the copy by +5 % in the active orientation (spec 005)
          duplicate.layout = offsetLayout(duplicate.layout, orientation);

          state.scene.sprites.push(duplicate);
          state.selection = { ids: [duplicate.id], type: 'sprite' };
          state.isDirty = true;
        });
      },

      setSpriteLayoutFields: (spriteId, orientation, fields) => {
        const sprite = get().scene?.sprites.find((s) => s.id === spriteId);
        if (!sprite) return;
        let layout = sprite.layout;
        if (finite(fields.x)) layout = setPositionField(layout, orientation, 'x', clampSpriteAxis(fields.x));
        if (finite(fields.y)) layout = setPositionField(layout, orientation, 'y', clampSpriteAxis(fields.y));
        const transform: { scale?: number; rotation?: number } = {};
        if (finite(fields.scale)) transform.scale = fields.scale;
        if (finite(fields.rotation)) transform.rotation = fields.rotation;
        if (Object.keys(transform).length > 0) layout = setOrientationTransform(layout, orientation, transform);
        if (layout === sprite.layout) return;
        set((state) => {
          const draft = state.scene?.sprites.find((s) => s.id === spriteId);
          if (!draft) return;
          draft.layout = layout;
          state.isDirty = true;
        });
      },

      setSpriteSize: (spriteId, size) => {
        if (!(size.width > 0) || !(size.height > 0)) return;
        set((state) => {
          const sprite = state.scene?.sprites.find((s) => s.id === spriteId);
          if (!sprite) return;
          if (sprite.size && sprite.size.width === size.width && sprite.size.height === size.height) return;
          sprite.size = { width: size.width, height: size.height };
          state.isDirty = true;
        });
      },

      setSpriteHinge: (spriteId, hinge) => {
        const sprite = get().scene?.sprites.find((s) => s.id === spriteId);
        if (!sprite) return;
        const oldHinge = sprite.hinge ?? { x: 0.5, y: 0.5 };
        const newHinge = hinge
          ? { x: Math.min(1, Math.max(0, hinge.x)), y: Math.min(1, Math.max(0, hinge.y)) }
          : { x: 0.5, y: 0.5 };
        if (oldHinge.x === newHinge.x && oldHinge.y === newHinge.y && (hinge || !sprite.hinge)) return;
        const layout = compensateHinge(sprite, oldHinge, newHinge);
        set((state) => {
          const draft = state.scene?.sprites.find((s) => s.id === spriteId);
          if (!draft) return;
          if (hinge) draft.hinge = newHinge;
          else delete draft.hinge;
          draft.layout = layout;
          state.isDirty = true;
        });
      },

      addSpriteState: (spriteId, name) => {
        const trimmed = name.trim();
        if (!trimmed) return 'empty';
        const sprite = get().scene?.sprites.find((s) => s.id === spriteId);
        if (!sprite) return 'missing';
        if (sprite.states.some((s) => s.name.trim() === trimmed)) return 'duplicate';
        set((state) => {
          const draft = state.scene?.sprites.find((s) => s.id === spriteId);
          if (!draft) return;
          draft.states.push({
            name: trimmed,
            mediaId: 0,
            animation: { type: 'none', duration: 1, repeat: 0 },
          });
          state.isDirty = true;
        });
        return null;
      },

      removeSpriteState: (spriteId, stateName) => {
        const sprite = get().scene?.sprites.find((s) => s.id === spriteId);
        if (!sprite || !sprite.states.some((s) => s.name === stateName)) return 'missing';
        if (sprite.states.length <= 1) return 'last';
        set((state) => {
          const draft = state.scene?.sprites.find((s) => s.id === spriteId);
          if (!draft) return;
          draft.states = draft.states.filter((s) => s.name !== stateName);
          if (draft.defaultState === stateName) draft.defaultState = draft.states[0].name;
          state.isDirty = true;
        });
        return null;
      },

      setDefaultSpriteState: (spriteId, stateName) => {
        set((state) => {
          const sprite = state.scene?.sprites.find((s) => s.id === spriteId);
          if (!sprite || !sprite.states.some((s) => s.name === stateName)) return;
          if (sprite.defaultState === stateName) return;
          sprite.defaultState = stateName;
          state.isDirty = true;
        });
      },

      renameSpriteState: (spriteId, from, to) => {
        const name = to.trim();
        if (!name) return 'empty';
        const sprite = get().scene?.sprites.find((s) => s.id === spriteId);
        if (!sprite || !sprite.states.some((s) => s.name === from)) return 'missing';
        if (name === from) return null;
        if (sprite.states.some((s) => s.name !== from && s.name.trim() === name)) return 'duplicate';
        asOneStep(() => set((state) => {
          const draft = state.scene?.sprites.find((s) => s.id === spriteId);
          if (!draft) return;
          for (const st of draft.states) if (st.name === from) st.name = name;
          if (draft.defaultState === from) draft.defaultState = name;
          // The state's keys and the line expressions naming it follow the new name.
          const keys = state.keyframes.get(spriteId);
          if (keys?.some((k) => k.property === 'state' && k.value === from)) {
            state.keyframes.set(spriteId, keys.map((k) => (k.property === 'state' && k.value === from ? { ...k, value: name } : k)));
          }
          for (const dialog of state.scene!.dialogs) {
            for (const line of dialog.lines) {
              if (line.expression?.spriteId === spriteId && line.expression.state === from) line.expression.state = name;
            }
          }
          state.isDirty = true;
        }));
        return null;
      },

      assignStateMedia: (spriteId, stateName, mediaId, naturalSize) => {
        set((state) => {
          const sprite = state.scene?.sprites.find((s) => s.id === spriteId);
          const spriteState = sprite?.states.find((s) => s.name === stateName);
          if (!sprite || !spriteState) return;
          spriteState.mediaId = mediaId;
          if (!sprite.size && naturalSize && naturalSize.width > 0 && naturalSize.height > 0) {
            // A spritesheet sizes the sprite by its frame, never the whole sheet (spec 033).
            const picture = pictureSizeOf(mediaId, naturalSize);
            if (picture.width > 0 && picture.height > 0) sprite.size = sizeFromImage(picture);
          }
          state.isDirty = true;
        });
      },

      importSheetStates: (spriteId, items) => {
        const sprite = get().scene?.sprites.find((s) => s.id === spriteId);
        if (!sprite) return 'missing';
        const names = new Set(sprite.states.map((s) => s.name.trim()));
        for (const item of items) {
          const name = item.stateName.trim();
          if (item.create) {
            if (!name) return 'empty';
            if (names.has(name)) return 'duplicate';
            names.add(name);
          } else if (!sprite.states.some((s) => s.name === item.stateName)) {
            return 'missing';
          }
        }
        if (items.length === 0) return null;
        asOneStep(() => {
          const { addSpriteState, assignStateMedia } = get();
          for (const item of items) if (item.create) addSpriteState(spriteId, item.stateName);
          for (const item of items) assignStateMedia(spriteId, item.create ? item.stateName.trim() : item.stateName, item.mediaId, item.naturalSize);
        });
        return null;
      },

      setStatePlayback: (spriteId, stateName, playback) => {
        const sprite = get().scene?.sprites.find((s) => s.id === spriteId);
        const state = sprite?.states.find((s) => s.name === stateName);
        if (!sprite || !state) return 'that state no longer exists';
        if (JSON.stringify(state.playback ?? null) === JSON.stringify(playback ?? null)) return null;
        // The editSprite operation itself, called directly: playback is no
        // timing, and Dev times by hand (spec 035).
        const op = getOperation('editSprite')!;
        const args = { spriteId, setStates: [{ name: stateName, playback: playback ? playbackToArg(playback) : false }] };
        const invalid = validateAgainstSchema(op.inputSchema, args);
        if (invalid.length) return invalid.map((e) => e.message).join('; ');
        const result = op.apply(get().scene as unknown as SceneDocument, args, { newId: () => { throw new Error('editSprite creates no ids'); } });
        if (result.ok === false) return result.errors.map((e) => e.message).join('; ');
        const doc = result.doc;
        asOneStep(() => set((st) => {
          st.scene = doc as Story;
          st.isDirty = true;
        }));
        return null;
      },

      applyStateSheet: (spriteId, stateName, change) => {
        const sprite = get().scene?.sprites.find((s) => s.id === spriteId);
        if (!sprite || !sprite.states.some((s) => s.name === stateName)) return 'that state no longer exists';
        let refusal: string | null = null;
        asOneStep(() => {
          if (change.mediaId !== undefined) get().assignStateMedia(spriteId, stateName, change.mediaId, change.naturalSize);
          if (change.playback !== undefined) refusal = get().setStatePlayback(spriteId, stateName, change.playback ?? undefined);
        });
        return refusal;
      },

      // =========================================================================
      // DIALOG OPERATIONS
      // =========================================================================

      addDialog: (dialogData?: Partial<Dialog>) => {
        const dialog = createDefaultDialog();
        if (dialogData) {
          Object.assign(dialog, dialogData);
        }

        set((state) => {
          if (!state.scene) return;
          state.scene.dialogs.push(dialog);
          state.selection = { ids: [dialog.id], type: 'dialog' };
          state.isDirty = true;
        });
      },

      updateDialog: (dialogId: string, updates: Partial<Dialog>) => {
        set((state) => {
          if (!state.scene) return;
          const dialog = state.scene.dialogs.find((d) => d.id === dialogId);
          if (!dialog) return;
          Object.assign(dialog, updates);
          state.isDirty = true;
        });
      },

      setLineExpression: (dialogId: string, lineId: string, value?: LineExpression) => {
        const line = get().scene?.dialogs.find((d) => d.id === dialogId)?.lines.find((l) => l.id === lineId);
        if (!line) return;
        const cur = line.expression;
        const same = value
          ? !!cur && cur.spriteId === value.spriteId && cur.state === value.state && cur.backToDefault === value.backToDefault
          : !cur;
        if (same) return;
        set((state) => {
          const draft = state.scene?.dialogs.find((d) => d.id === dialogId)?.lines.find((l) => l.id === lineId);
          if (!draft) return;
          if (value) draft.expression = { spriteId: value.spriteId, state: value.state, backToDefault: value.backToDefault };
          else delete draft.expression;
          state.isDirty = true;
        });
      },

      attachLineVoice: (dialogId: string, lineId: string, mediaId: number, durationMs?: number) => {
        const dialog = get().scene?.dialogs.find((d) => d.id === dialogId);
        const line = dialog?.lines.find((l) => l.id === lineId);
        if (!dialog || !line || !(mediaId > 0)) return;
        const next = fitLineToVoice({ ...line, audioId: mediaId }, durationMs, lineInMs(dialog, line));
        if (next.audioId === line.audioId && next.duration === line.duration) return;
        set((state) => {
          const draft = state.scene?.dialogs.find((d) => d.id === dialogId)?.lines.find((l) => l.id === lineId);
          if (!draft) return;
          draft.audioId = mediaId;
          if (next.duration !== undefined) draft.duration = next.duration;
          state.isDirty = true;
        });
      },

      removeLineVoice: (dialogId: string, lineId: string) => {
        const line = get().scene?.dialogs.find((d) => d.id === dialogId)?.lines.find((l) => l.id === lineId);
        if (!line || line.audioId === undefined) return;
        set((state) => {
          const draft = state.scene?.dialogs.find((d) => d.id === dialogId)?.lines.find((l) => l.id === lineId);
          if (!draft) return;
          delete draft.audioId;
          state.isDirty = true;
        });
      },

      setLineDuration: (dialogId: string, lineId: string, seconds: number) => {
        const line = get().scene?.dialogs.find((d) => d.id === dialogId)?.lines.find((l) => l.id === lineId);
        if (!line || !Number.isFinite(seconds)) return;
        const value = Math.min(120, Math.max(0.1, seconds));
        if (line.duration === value && line.durationSet) return;
        set((state) => {
          const draft = state.scene?.dialogs.find((d) => d.id === dialogId)?.lines.find((l) => l.id === lineId);
          if (!draft) return;
          draft.duration = value;
          draft.durationSet = true;
          state.isDirty = true;
        });
      },

      fitLineToVoiceLength: (dialogId: string, lineId: string, durationMs: number) => {
        const dialog = get().scene?.dialogs.find((d) => d.id === dialogId);
        const line = dialog?.lines.find((l) => l.id === lineId);
        if (!dialog || !line) return;
        const { durationSet: _set, ...rest } = line;
        const next = fitLineToVoice(rest, durationMs, lineInMs(dialog, line));
        if (next === rest && !line.durationSet) return;
        set((state) => {
          const draft = state.scene?.dialogs.find((d) => d.id === dialogId)?.lines.find((l) => l.id === lineId);
          if (!draft) return;
          delete draft.durationSet;
          if (next.duration !== undefined) draft.duration = next.duration;
          state.isDirty = true;
        });
      },

      deleteDialog: (dialogId: string) => {
        set((state) => {
          if (!state.scene) return;
          const index = state.scene.dialogs.findIndex((d) => d.id === dialogId);
          if (index === -1) return;
          state.scene.dialogs.splice(index, 1);
          
          if (state.selection.ids.includes(dialogId)) {
            state.selection.ids = state.selection.ids.filter((id) => id !== dialogId);
            if (state.selection.ids.length === 0) {
              // Default to scene selection
              state.selection = { ids: ['scene'], type: 'scene' };
            }
          }
          
          state.isDirty = true;
        });
      },

      // =========================================================================
      // SOUND OPERATIONS
      // =========================================================================

      addSound: (soundData: Partial<Sound>) => {
        const sound = createDefaultSound(soundData.type || 'sfx', soundData.mediaId || 0);
        Object.assign(sound, soundData);

        set((state) => {
          if (!state.scene) return;
          state.scene.sounds.push(sound);
          state.selection = { ids: [sound.id], type: 'sound' };
          state.isDirty = true;
        });
      },

      updateSound: (soundId: string, updates: Partial<Sound>) => {
        set((state) => {
          if (!state.scene) return;
          const sound = state.scene.sounds.find((s) => s.id === soundId);
          if (!sound) return;
          Object.assign(sound, updates);
          state.isDirty = true;
        });
      },

      setSoundType: (soundId, type) => {
        set((state) => {
          const sound = state.scene?.sounds.find((s) => s.id === soundId);
          if (!sound || sound.type === type) return;
          Object.assign(sound, soundTypeChange(sound, type));
          state.isDirty = true;
        });
      },

      setSoundTiming: (soundId, patch) => {
        set((state) => {
          if (!state.scene) return;
          const sound = state.scene.sounds.find((s) => s.id === soundId);
          if (!sound) return;
          const next = clampSoundTiming(sound, patch, previewDurationMs(state.scene));
          sound.startTime = next.startTime;
          if (next.endTime === undefined) delete sound.endTime;
          else sound.endTime = next.endTime;
          sound.fadeIn = next.fadeIn;
          sound.fadeOut = next.fadeOut;
          state.isDirty = true;
        });
      },

      deleteSound: (soundId: string) => {
        set((state) => {
          if (!state.scene) return;
          const index = state.scene.sounds.findIndex((s) => s.id === soundId);
          if (index === -1) return;
          state.scene.sounds.splice(index, 1);
          
          if (state.selection.ids.includes(soundId)) {
            state.selection.ids = state.selection.ids.filter((id) => id !== soundId);
            if (state.selection.ids.length === 0) {
              // Default to scene selection
              state.selection = { ids: ['scene'], type: 'scene' };
            }
          }
          state.isDirty = true;
        });
      },

      // =========================================================================
      // SIGNAL OPERATIONS
      // =========================================================================

      addSignal: (signalData: Partial<Signal>) => {
        const signal: Signal = {
          id: `signal_${Date.now()}`,
          type: signalData.type || 'custom',
          trigger: signalData.trigger || { time: 0 },
          payload: signalData.payload || {},
          priority: signalData.priority || 0,
        };

        set((state) => {
          if (!state.scene) return;
          state.scene.signals.push(signal);
          state.isDirty = true;
        });
      },

      updateSignal: (signalId: string, updates: Partial<Signal>) => {
        set((state) => {
          if (!state.scene) return;
          const signal = state.scene.signals.find((s) => s.id === signalId);
          if (!signal) return;
          Object.assign(signal, updates);
          state.isDirty = true;
        });
      },

      deleteSignal: (signalId: string) => {
        set((state) => {
          if (!state.scene) return;
          const index = state.scene.signals.findIndex((s) => s.id === signalId);
          if (index === -1) return;
          state.scene.signals.splice(index, 1);
          if (state.selection.ids.includes(signalId)) {
            state.selection.ids = state.selection.ids.filter((id) => id !== signalId);
            if (state.selection.ids.length === 0) state.selection = { ids: ['scene'], type: 'scene' };
          }
          state.isDirty = true;
        });
      },

      // =========================================================================
      // BACKGROUND OPERATIONS
      // =========================================================================

      updateBackground: (updates: Partial<Background>) => {
        set((state) => {
          if (!state.scene) return;
          // Merge; a key given as undefined is removed (the editor's "Remove" base image)
          const bg = state.scene.background as unknown as Record<string, unknown>;
          for (const [key, value] of Object.entries(updates)) {
            if (value === undefined) delete bg[key];
            else bg[key] = value;
          }
          state.isDirty = true;
        });
      },

      // Background states (spec 040): through 039's operation, one undo step each.
      addBackgroundState: (name) => {
        const bg = get().scene?.background;
        if (!bg) return 'missing';
        const trimmed = name.trim();
        if (!trimmed) return 'empty';
        if (trimmed.length > BACKGROUND_STATE_NAME_MAX) return 'long';
        const states = backgroundStates(bg);
        if (states.some((s) => s.name.trim().toLowerCase() === trimmed.toLowerCase())) return 'duplicate';
        return writeBackgroundStatesStep(get, [...stateArgs(states), { name: trimmed }], backgroundDefaultState(bg));
      },

      removeBackgroundState: (name) => {
        const bg = get().scene?.background;
        const states = backgroundStates(bg);
        if (!bg || !states.some((s) => s.name === name)) return 'missing';
        if (states.length <= 1) return 'last';
        const def = backgroundDefaultState(bg);
        return writeBackgroundStatesStep(get, stateArgs(states.filter((s) => s.name !== name)), def === name ? undefined : def);
      },

      renameBackgroundState: (from, to) => {
        const { scene, keyframes } = get();
        const states = backgroundStates(scene?.background);
        if (!scene || !states.some((s) => s.name === from)) return 'missing';
        const name = to.trim();
        if (!name) return 'empty';
        if (name.length > BACKGROUND_STATE_NAME_MAX) return 'long';
        if (name === from) return null;
        if (states.some((s) => s.name !== from && s.name.trim().toLowerCase() === name.toLowerCase())) return 'duplicate';
        const result = renameBackgroundStateOf({ background: scene.background, keyframes: keyframesToArray(keyframes) }, from, name);
        if (result.ok === false) return 'missing';
        const { background, keyframes: tracks } = result.doc;
        asOneStep(() => set((state) => {
          if (!state.scene) return;
          state.scene.background = background;
          if (tracks) state.keyframes = new Map(tracks.filter((t) => t.keyframes.length > 0).map((t) => [t.trackId, t.keyframes] as const));
          state.isDirty = true;
        }));
        return null;
      },

      setDefaultBackgroundState: (name) => {
        const bg = get().scene?.background;
        const states = backgroundStates(bg);
        if (!bg || !states.some((s) => s.name === name)) return 'missing';
        if (backgroundDefaultState(bg) === name && bg.defaultState === name) return null;
        return writeBackgroundStatesStep(get, stateArgs(states), name);
      },

      assignBackgroundStateMedia: (name, mediaId) => {
        const bg = get().scene?.background;
        const states = backgroundStates(bg);
        if (!bg || !states.some((s) => s.name === name)) return 'missing';
        const id = Number.isInteger(mediaId) && mediaId > 0 ? mediaId : undefined;
        const next = states.map((s) => (s.name === name ? { name: s.name, ...(id ? { mediaId: id } : {}) } : s));
        return writeBackgroundStatesStep(get, stateArgs(next), backgroundDefaultState(bg));
      },

      // =========================================================================
      // SCENE PROPERTIES OPERATIONS
      // =========================================================================

      updateSceneProperties: (updates: { name?: string; enterTransition?: Transition; exitTransition?: Transition }) => {
        set((state) => {
          if (!state.scene) return;
          if (updates.name !== undefined) {
            state.scene.name = updates.name;
          }
          if (updates.enterTransition !== undefined) {
            state.scene.enterTransition = updates.enterTransition;
          }
          if (updates.exitTransition !== undefined) {
            state.scene.exitTransition = updates.exitTransition;
          }
          state.isDirty = true;
        });
      },

      renameScene: (name: string) => {
        const next = normaliseSceneName(name);
        const current = get().scene;
        if (!current || !next || next === current.name) return;
        set((state) => {
          if (!state.scene) return;
          state.scene.name = next;
          state.isDirty = true;
        });
      },

      // =========================================================================
      // UNDO / REDO (spec 018)
      // =========================================================================

      undo: () => {
        // A gesture still open is finished first, so it is the step undone.
        while (historyGroupDepth > 0) endHistoryGroup();
        const t = useSceneStore.temporal.getState();
        if (t.pastStates.length === 0) return;
        t.undo();
        afterHistoryMove();
      },

      redo: () => {
        while (historyGroupDepth > 0) endHistoryGroup();
        const t = useSceneStore.temporal.getState();
        if (t.futureStates.length === 0) return;
        t.redo();
        afterHistoryMove();
      },

      beginHistoryGroup,
      endHistoryGroup,

      // =========================================================================
      // SELECTION
      // =========================================================================

      // Selection is editor UI state: it never marks the scene dirty and is
      // never an undo step (spec 018).
      select: (ids: string[], type: ElementType) => {
        set((state) => {
          state.selection = { ids, type };
        });
      },

      selectSingle: (id: string, type: ElementType) => {
        set((state) => {
          state.selection = { ids: [id], type };
        });
      },

      addToSelection: (id: string) => {
        set((state) => {
          if (!state.selection.ids.includes(id)) {
            state.selection.ids.push(id);
          }
        });
      },

      removeFromSelection: (id: string) => {
        set((state) => {
          state.selection.ids = state.selection.ids.filter((i) => i !== id);
          if (state.selection.ids.length === 0) {
            // Default to scene selection instead of null
            state.selection = { ids: ['scene'], type: 'scene' };
          }
        });
      },

      clearSelection: () => {
        set((state) => {
          state.selection = { ids: ['scene'], type: 'scene' };
        });
      },

      // =========================================================================
      // CLIPBOARD
      // =========================================================================

      copy: () => {
        const { selection, scene } = get();
        if (!scene || selection.ids.length === 0) return;

        let data: unknown[] = [];
        
        switch (selection.type) {
          case 'sprite':
            data = scene.sprites.filter((s) => selection.ids.includes(s.id));
            break;
          case 'dialog':
            data = scene.dialogs.filter((d) => selection.ids.includes(d.id));
            break;
          case 'sound':
            data = scene.sounds.filter((s) => selection.ids.includes(s.id));
            break;
        }

        set((state) => {
          state.clipboard = {
            type: selection.type,
            data: JSON.parse(JSON.stringify(data)),
          };
        });
      },

      paste: () => {
        const { clipboard, previewOrientation } = get();
        if (!clipboard || clipboard.data.length === 0) return;

        set((state) => {
          if (!state.scene) return;

          const newIds: string[] = [];
          const orientation = previewOrientation;

          clipboard.data.forEach((item) => {
            const newId = `${clipboard.type}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
            const clone = JSON.parse(JSON.stringify(item));
            clone.id = newId;

            // Offset position for visual distinction (for sprites with layout)
            if ('layout' in clone && clone.layout) {
              clone.layout = offsetLayout(clone.layout, orientation);
            }

            switch (clipboard.type) {
              case 'sprite':
                clone.name = `${clone.name} (Copy)`;
                state.scene!.sprites.push(clone);
                break;
              case 'dialog':
                state.scene!.dialogs.push(clone);
                break;
              case 'sound':
                state.scene!.sounds.push(clone);
                break;
            }

            newIds.push(newId);
          });

          state.selection = { ids: newIds, type: clipboard.type };
          state.isDirty = true;
        });
      },

      cut: () => {
        get().copy();
        get().deleteSelected();
      },

      deleteSelected: () => {
        const { selection } = get();
        if (selection.ids.length === 0) return;

        set((state) => {
          if (!state.scene) return;

          switch (state.selection.type) {
            case 'sprite':
              state.scene.sprites = state.scene.sprites.filter((s) => !state.selection.ids.includes(s.id));
              break;
            case 'dialog':
              state.scene.dialogs = state.scene.dialogs.filter((d) => !state.selection.ids.includes(d.id));
              break;
            case 'sound':
              state.scene.sounds = state.scene.sounds.filter((s) => !state.selection.ids.includes(s.id));
              break;
            case 'signal':
              state.scene.signals = state.scene.signals.filter((s) => !state.selection.ids.includes(s.id));
              break;
          }

          state.selection = { ids: [], type: null };
          state.isDirty = true;
        });
      },

      // =========================================================================
      // PLAYBACK
      // =========================================================================

      play: () => {
        const { isPlaying, scene, keyframes, playheadPosition } = get();
        if (isPlaying) return;
        const start = previewStartFor(scene, keyframes, playheadPosition);
        set((state) => {
          state.previewStartMs = start;
          state.playheadPosition = start;
          state.isPlaying = true;
          state.choiceHold = false;
        });
      },

      playFromStart: () => {
        if (get().isPlaying) {
          get().jumpPreview(0);
          return;
        }
        set((state) => {
          state.previewStartMs = 0;
          state.playheadPosition = 0;
          state.isPlaying = true;
          state.choiceHold = false;
        });
      },

      jumpPreview: (ms: number, options?: { hold?: boolean }) => {
        set((state) => {
          if (!state.isPlaying) return;
          const t = Math.max(0, Number.isFinite(ms) ? ms : 0);
          state.playheadPosition = t;
          state.choiceHold = false;
          state.previewJump = { ms: t, seq: (state.previewJump?.seq ?? 0) + 1, hold: options?.hold === true };
        });
      },

      pause: () => {
        get().stop();
      },

      stop: () => {
        set((state) => {
          // Previewing: the playhead stays where the preview was (D-1); else ⏹ parks it at 0
          if (!state.isPlaying) state.playheadPosition = 0;
          state.isPlaying = false;
          state.choiceHold = false;
        });
      },

      holdAtChoices: (revealMs: number) => {
        set((state) => {
          if (!state.isPlaying) return;
          state.playheadPosition = revealMs;
          state.choiceHold = true;
        });
      },

      setPlayhead: (position: number) => {
        set((state) => {
          state.playheadPosition = position;
        });
      },

      // =========================================================================
      // KEYFRAME OPERATIONS
      // =========================================================================

      addKeyframe: (trackId: string, keyframe: Omit<Keyframe, 'id'>) => {
        const id = newKeyframeId();
        const kf: Keyframe = { ...keyframe, id, time: Math.max(0, Math.round(keyframe.time)) };
        if (kf.orientation === undefined) delete kf.orientation;
        set((state) => {
          const existing = state.keyframes.get(trackId) || [];
          state.keyframes.set(trackId, writeKeyframe(existing, kf, newKeyframeId));
          state.isDirty = true;
        });
        return id;
      },

      applySpriteMotionPreset: (spriteId, preset, orientation) => {
        const sprite = get().scene?.sprites.find((s) => s.id === spriteId);
        if (!sprite) return [];
        const before = get().keyframes.get(spriteId) || [];
        const writtenIds = new Set<string>();
        const newId = () => {
          const id = newKeyframeId();
          writtenIds.add(id);
          return id;
        };
        const next = applyMotionPreset(before, sprite, preset, { orientation, newId });
        const written = next.filter((k) => writtenIds.has(k.id));
        if (written.length === 0) return [];
        set((state) => {
          state.keyframes.set(spriteId, next);
          state.isDirty = true;
        });
        return written;
      },

      getKeyframeValueAt: (trackId, property, time, orientation) => {
        const sprite = get().scene?.sprites.find((s) => s.id === trackId);
        return sprite ? keyframeValueAt(sprite, get().keyframes.get(trackId), property, time, orientation) : undefined;
      },

      addKeyframeAtPlayhead: (trackId, property) => {
        const { playheadPosition, previewOrientation, getKeyframeValueAt, addKeyframe } = get();
        const time = Math.max(0, Math.round(playheadPosition));
        if (trackId === BACKGROUND_TRACK_ID) {
          // Spec 040 D-3: a change to the state after the one playing, faded over 500 ms, for every orientation.
          const { scene, keyframes } = get();
          const states = backgroundStates(scene?.background);
          if (property !== BACKGROUND_STATE_PROPERTY || states.length < 2) return null;
          const playing = backgroundStateAt(scene!.background, keyframesToArray(keyframes), time).state;
          const at = states.findIndex((s) => s.name === playing);
          const next = states[(at + 1) % states.length].name;
          return addKeyframe(trackId, { time, property, value: next, transition: { ...DEFAULT_BACKGROUND_CHANGE } });
        }
        const value = getKeyframeValueAt(trackId, property, time, previewOrientation);
        if (value === undefined) return null;
        return addKeyframe(trackId, {
          time,
          property,
          value,
          easing: 'linear',
          orientation: previewOrientation === 'desktop' ? undefined : previewOrientation,
        });
      },

      updateKeyframe: (trackId: string, keyframeId: string, updates: Partial<Keyframe>, orientation?: Orientation) => {
        const existing = get().keyframes.get(trackId);
        const target = existing?.find((kf) => kf.id === keyframeId);
        if (!existing || !target) return null;
        let editedId = keyframeId;
        const background = trackId === BACKGROUND_TRACK_ID;
        set((state) => {
          let kfs = state.keyframes.get(trackId) ?? [];
          // Background state keys carry no orientation (spec 039 D-3): they never fork.
          if (!background && isTaggedEdit(orientation) && isUntaggedKeyframe(target)) {
            const fork = forkOrientation(kfs, target.property, orientation, newKeyframeId);
            kfs = fork.keyframes;
            editedId = fork.copies.get(keyframeId) ?? keyframeId;
          }
          const current = kfs.find((kf) => kf.id === editedId);
          if (!current) return;
          const { id: _ignored, ...rest } = updates;
          // A transition belongs to a background state key only (spec 040).
          if ('transition' in rest && !(background && current.property === BACKGROUND_STATE_PROPERTY)) delete rest.transition;
          const next: Keyframe = { ...current, ...rest };
          if (next.transition === undefined) delete next.transition;
          if (rest.time !== undefined) next.time = Math.max(0, Math.round(rest.time));
          state.keyframes.set(trackId, putKeyframe(kfs.filter((kf) => kf.id !== editedId), next));
          state.isDirty = true;
        });
        return editedId;
      },

      deleteKeyframe: (trackId: string, keyframeId: string) => {
        set((state) => {
          const existing = state.keyframes.get(trackId);
          if (!existing) return;
          
          state.keyframes.set(trackId, existing.filter(kf => kf.id !== keyframeId));
          state.isDirty = true;
        });
      },

      getKeyframesForTrack: (trackId: string) => {
        return get().keyframes.get(trackId) || [];
      },

      moveKeyframes: (items, deltaMs, maxTime) => {
        const idMap = new Map<string, string>();
        if (items.length === 0 || !Number.isFinite(deltaMs)) return idMap;
        const orientation = get().previewOrientation;
        const byTrack = new Map<string, Set<string>>();
        for (const { trackId, keyframeId } of items) {
          if (!byTrack.has(trackId)) byTrack.set(trackId, new Set());
          byTrack.get(trackId)!.add(keyframeId);
        }
        const upper = maxTime !== undefined && Number.isFinite(maxTime) ? Math.round(maxTime) : Infinity;
        asOneStep(() => set((state) => {
          byTrack.forEach((ids, trackId) => {
            let kfs = state.keyframes.get(trackId);
            if (!kfs) return;
            // Fork first: an untagged keyframe moved in portrait/landscape
            // moves as that orientation's copy (spec 006). A property forks
            // once, copying all its untagged keyframes, so the copies are
            // kept for every later keyframe of that property in the batch.
            const moved = new Set<string>();
            const forkCopies = new Map<string, string>();
            for (const id of ids) {
              const kf = kfs.find((k) => k.id === id);
              if (!kf) continue;
              let movedId = id;
              if (trackId !== BACKGROUND_TRACK_ID && isTaggedEdit(orientation) && isUntaggedKeyframe(kf)) {
                if (!forkCopies.has(id)) {
                  const fork = forkOrientation(kfs, kf.property, orientation, newKeyframeId);
                  kfs = fork.keyframes;
                  fork.copies.forEach((copyId, originalId) => forkCopies.set(originalId, copyId));
                }
                movedId = forkCopies.get(id) ?? id;
              }
              moved.add(movedId);
              idMap.set(id, movedId);
            }
            // Then move them all at once, so a keyframe moving out of a slot
            // is never replaced by one moving into it; a moved keyframe that
            // lands on a keyframe left in place replaces it.
            let next = kfs.filter((k) => !moved.has(k.id));
            for (const k of kfs) {
              if (!moved.has(k.id)) continue;
              const time = Math.min(upper, Math.max(0, Math.round(k.time + deltaMs)));
              next = putKeyframe(next, { ...k, time });
            }
            state.keyframes.set(trackId, next);
          });
          state.isDirty = true;
          const remap = (sk: { trackId: string; keyframeId: string }) =>
            idMap.has(sk.keyframeId) ? { trackId: sk.trackId, keyframeId: idMap.get(sk.keyframeId)! } : sk;
          state.selectedKeyframes = state.selectedKeyframes.map(remap);
          if (state.selectedKeyframe) state.selectedKeyframe = remap(state.selectedKeyframe);
        }));
        return idMap;
      },

      deleteKeyframes: (items) => {
        if (items.length === 0) return;
        const doomed = new Set(items.map((i) => `${i.trackId}\u0000${i.keyframeId}`));
        const hit = (trackId: string, keyframeId: string) => doomed.has(`${trackId}\u0000${keyframeId}`);
        asOneStep(() => set((state) => {
          let changed = false;
          state.keyframes.forEach((kfs, trackId) => {
            const kept = kfs.filter((k) => !hit(trackId, k.id));
            if (kept.length !== kfs.length) {
              state.keyframes.set(trackId, kept);
              changed = true;
            }
          });
          if (changed) state.isDirty = true;
          state.selectedKeyframes = state.selectedKeyframes.filter((sk) => !hit(sk.trackId, sk.keyframeId));
          if (state.selectedKeyframe && hit(state.selectedKeyframe.trackId, state.selectedKeyframe.keyframeId)) {
            state.selectedKeyframe = state.selectedKeyframes[0] ?? null;
          }
        }));
      },

      copyKeyframes: (items) => {
        const { keyframes, selectedKeyframes } = get();
        const source = items ?? selectedKeyframes;
        const found: Array<{ trackId: string; kf: Keyframe }> = [];
        for (const { trackId, keyframeId } of source) {
          const kf = keyframes.get(trackId)?.find((k) => k.id === keyframeId);
          if (kf) found.push({ trackId, kf });
        }
        if (found.length === 0) return false;
        const earliest = Math.min(...found.map((f) => f.kf.time));
        const clip: KeyframeClipboardItem[] = found.map(({ trackId, kf }) => ({
          trackId,
          property: kf.property,
          offset: kf.time - earliest,
          value: kf.value,
          ...(kf.easing ? { easing: kf.easing } : {}),
          ...(kf.transition ? { transition: { ...kf.transition } } : {}),
        }));
        set((state) => {
          state.keyframeClipboard = clip;
        });
        return true;
      },

      pasteKeyframes: (trackId, rowProperty) => {
        const { keyframeClipboard, scene, playheadPosition, previewOrientation, addKeyframe } = get();
        if (!keyframeClipboard || keyframeClipboard.length === 0 || !scene) return [];
        const hasBackgroundRow = backgroundStates(scene.background).length >= 2;
        const trackExists = (id: string) => (id === BACKGROUND_TRACK_ID ? hasBackgroundRow : scene.sprites.some((s) => s.id === id));
        const rowProps = rowProperty ? (TIMELINE_ROW_PROPERTIES[rowProperty] ?? [rowProperty]) : null;
        // Background keys paste within the Background row only, sprite keys never onto it (spec 040).
        const sameKind = (from: string) => trackId === undefined || (from === BACKGROUND_TRACK_ID) === (trackId === BACKGROUND_TRACK_ID);
        const items = keyframeClipboard
          .filter((c) => (rowProps ? rowProps.includes(c.property) : true))
          .filter((c) => sameKind(c.trackId))
          .map((c) => ({ ...c, trackId: trackId ?? c.trackId }))
          .filter((c) => trackExists(c.trackId));
        if (items.length === 0) return [];
        const base = Math.max(0, Math.round(playheadPosition));
        const orientation = previewOrientation === 'desktop' ? undefined : previewOrientation;
        const pasted: Array<{ trackId: string; keyframeId: string }> = [];
        asOneStep(() => {
          for (const c of items) {
            const keyframeId = c.trackId === BACKGROUND_TRACK_ID
              ? addKeyframe(c.trackId, {
                time: base + c.offset,
                property: c.property,
                value: c.value,
                ...(c.transition ? { transition: { ...c.transition } } : {}),
              })
              : addKeyframe(c.trackId, {
                time: base + c.offset,
                property: c.property,
                value: c.value,
                easing: c.easing ?? 'linear',
                orientation,
              });
            pasted.push({ trackId: c.trackId, keyframeId });
          }
        });
        get().setKeyframeSelection(pasted);
        return pasted;
      },

      isPropertyAnimated: (spriteId, property) => {
        const { scene, keyframes, previewOrientation } = get();
        if (!scene) return false;
        return candidateKeyframes(keyframes.get(spriteId) ?? [], property, previewOrientation).length > 0;
      },

      commitSpriteTransform: (spriteId, changes) => {
        const { scene, previewOrientation: orientation, playheadPosition } = get();
        const sprite = scene?.sprites.find((s) => s.id === spriteId);
        if (!scene || !sprite) return;
        const wanted: Array<[string, number]> = [];
        if (finite(changes.position?.x)) wanted.push(['position.x', clampSpriteAxis(changes.position!.x!)]);
        if (finite(changes.position?.y)) wanted.push(['position.y', clampSpriteAxis(changes.position!.y!)]);
        if (finite(changes.scale)) wanted.push(['transform.scale', changes.scale!]);
        if (finite(changes.rotation)) wanted.push(['transform.rotation', changes.rotation!]);
        if (wanted.length === 0) return;
        const time = Math.max(0, Math.round(playheadPosition));
        const tag = orientation === 'desktop' ? undefined : orientation;
        const layoutFields: SpriteLayoutFields = {};
        asOneStep(() => {
          for (const [property, value] of wanted) {
            if (get().isPropertyAnimated(spriteId, property)) {
              // Auto-key: a keyframe at the playhead (an existing one there
              // keeps its easing; an untagged one forks first, spec 006).
              const atTime = candidateKeyframes(get().keyframes.get(spriteId) ?? [], property, orientation)
                .find((k) => k.time === time);
              if (atTime) get().updateKeyframe(spriteId, atTime.id, { value }, orientation);
              else get().addKeyframe(spriteId, { time, property, value, easing: 'linear', orientation: tag });
            } else {
              const field = ({ 'position.x': 'x', 'position.y': 'y', 'transform.scale': 'scale', 'transform.rotation': 'rotation' } as const)[property as 'position.x'];
              layoutFields[field] = value;
            }
          }
          if (Object.keys(layoutFields).length > 0) get().setSpriteLayoutFields(spriteId, orientation, layoutFields);
        });
      },

      commitSpriteFlip: (spriteId, axis, value) => {
        const { scene, previewOrientation: orientation, playheadPosition } = get();
        const sprite = scene?.sprites.find((s) => s.id === spriteId);
        if (!scene || !sprite) return;
        const property = axis === 'x' ? 'transform.flipX' : 'transform.flipY';
        asOneStep(() => {
          if (get().isPropertyAnimated(spriteId, property)) {
            const time = Math.max(0, Math.round(playheadPosition));
            const atTime = candidateKeyframes(get().keyframes.get(spriteId) ?? [], property, orientation)
              .find((k) => k.time === time);
            const keyValue = value ? 1 : 0;
            if (atTime) get().updateKeyframe(spriteId, atTime.id, { value: keyValue }, orientation);
            else get().addKeyframe(spriteId, { time, property, value: keyValue, orientation: orientation === 'desktop' ? undefined : orientation });
            return;
          }
          const layout = setAtRestFlip(sprite.layout, axis, value);
          set((state) => {
            const draft = state.scene?.sprites.find((s) => s.id === spriteId);
            if (!draft) return;
            draft.layout = layout;
            state.isDirty = true;
          });
        });
      },

      nudgeSprite: (spriteId, dxPx, dyPx) => {
        const st = get();
        const sprite = st.scene?.sprites.find((s) => s.id === spriteId);
        if (!sprite || (!dxPx && !dyPx)) return;
        const o = st.previewOrientation;
        const stage = stageSize(o);
        const current = (property: 'position.x' | 'position.y') =>
          st.isPropertyAnimated(spriteId, property)
            ? Number(st.getKeyframeValueAt(spriteId, property, Math.max(0, Math.round(st.playheadPosition)), o))
            : (keyframeBaseValue(sprite, property, o) as number);
        const position: { x?: number; y?: number } = {};
        if (dxPx) position.x = current('position.x') + (dxPx / stage.width) * 100;
        if (dyPx) position.y = current('position.y') + (dyPx / stage.height) * 100;

        const now = Date.now();
        const coalesce = lastNudge !== null &&
          lastNudge.spriteId === spriteId &&
          now - lastNudge.at < NUDGE_COALESCE_MS &&
          lastNudge.scene === st.scene &&
          lastNudge.keyframes === st.keyframes;
        const temporalStore = useSceneStore.temporal;
        const pastBefore = temporalStore.getState().pastStates;
        st.commitSpriteTransform(spriteId, { position });
        const pastAfter = temporalStore.getState().pastStates;
        if (coalesce && pastAfter !== pastBefore && pastAfter.length > 0 &&
            pastAfter[pastAfter.length - 1] !== pastBefore[pastBefore.length - 1]) {
          // Fold this nudge into the step the first nudge of the run recorded.
          temporalStore.setState({ pastStates: pastAfter.slice(0, -1) });
        }
        const after = get();
        lastNudge = { spriteId, at: now, scene: after.scene, keyframes: after.keyframes };
      },

      getPlaybackKeyframes: (orientation?: Orientation) => {
        const { scene, keyframes, previewOrientation } = get();
        return playbackKeyframeMap(scene, keyframes, orientation ?? previewOrientation);
      },

      getPlayableScene: () => {
        const { scene, keyframes } = get();
        if (!scene) return null;
        // The cuts of its pictures (spec 033 D-3), as far as they are loaded.
        return toPlayableScene({ ...(scene as unknown as PlayableSceneInput), keyframes: keyframesToArray(keyframes) }, { sheets: knownSheets() });
      },

      getInterpolatedValue: (trackId: string, property: string, time: number, orientation?: Orientation) => {
        const o = orientation ?? get().previewOrientation;
        return interpolateProperty(get().getPlaybackKeyframes(o).get(trackId), property, time, o);
      },

      // =========================================================================
      // UI STATE
      // =========================================================================

      setPreviewOrientation: (orientation) => {
        set((state) => {
          state.previewOrientation = orientation;
        });
        // Immediately flush to IndexedDB (bypass debounce) so a quick reload preserves the choice
        const state = get();
        if (state.scene) {
          if (localSaveTimeout) {
            clearTimeout(localSaveTimeout);
            localSaveTimeout = null;
          }
          writeLocalEditorStateSafely(state.scene.id, state.getEditorStateData());
        }
      },

      setViewportZoom: (zoom) => {
        set((state) => {
          state.viewportZoom = clamp(zoom, ...VIEWPORT_ZOOM_RANGE);
        });
      },

      setViewportPan: (pan) => {
        set((state) => {
          state.viewportPan = pan;
        });
      },

      // =========================================================================
      // PANEL STATE
      // =========================================================================
      
      setTimelineHeight: (height: number) => {
        set((state) => {
          state.timelineHeight = clamp(height, TIMELINE_MIN_HEIGHT, timelineMaxHeight());
        });
      },
      
      setTimelineZoom: (zoom: number) => {
        set((state) => {
          state.timelineZoom = clamp(zoom, ...TIMELINE_ZOOM_RANGE);
        });
      },
      
      zoomTimeline: (direction) => {
        const { timelineZoom, setTimelineZoom } = get();
        setTimelineZoom(direction === 'in' ? timelineZoom * TIMELINE_ZOOM_STEP : timelineZoom / TIMELINE_ZOOM_STEP);
      },

      setTimelineScroll: (scrollLeft: number, scrollTop: number) => {
        set((state) => {
          state.timelineScrollLeft = scrollLeft;
          state.timelineScrollTop = scrollTop;
        });
      },
      
      setExpandedTracks: (tracks: Set<string>) => {
        set((state) => {
          state.expandedTracks = tracks;
        });
      },
      
      setCollapsedGroups: (groups: Set<string>) => {
        set((state) => {
          state.collapsedGroups = groups;
        });
      },
      
      toggleTrackExpanded: (trackId: string) => {
        set((state) => {
          if (state.expandedTracks.has(trackId)) {
            state.expandedTracks.delete(trackId);
          } else {
            state.expandedTracks.add(trackId);
          }
        });
      },
      
      toggleGroupCollapsed: (groupId: string) => {
        set((state) => {
          if (state.collapsedGroups.has(groupId)) {
            state.collapsedGroups.delete(groupId);
          } else {
            state.collapsedGroups.add(groupId);
          }
        });
      },
      
      setFocusedPane: (pane) => {
        set((state) => {
          state.focusedPane = pane;
        });
      },
      
      // =========================================================================
      // KEYFRAME SELECTION
      // =========================================================================
      
      selectKeyframe: (trackId: string, keyframeId: string) => {
        set((state) => {
          state.selectedKeyframe = { trackId, keyframeId };
          state.selectedKeyframes = [{ trackId, keyframeId }];
        });
      },
      
      toggleKeyframeInSelection: (trackId: string, keyframeId: string) => {
        set((state) => {
          const idx = state.selectedKeyframes.findIndex(sk => sk.keyframeId === keyframeId);
          if (idx >= 0) {
            state.selectedKeyframes.splice(idx, 1);
            // Update primary selectedKeyframe
            state.selectedKeyframe = state.selectedKeyframes.length > 0 ? state.selectedKeyframes[0] : null;
          } else {
            state.selectedKeyframes.push({ trackId, keyframeId });
            state.selectedKeyframe = { trackId, keyframeId };
          }
        });
      },
      
      setKeyframeSelection: (items: Array<{ trackId: string; keyframeId: string }>) => {
        set((state) => {
          state.selectedKeyframes = items;
          state.selectedKeyframe = items.length > 0 ? items[0] : null;
        });
      },
      
      clearKeyframeSelection: () => {
        set((state) => {
          state.selectedKeyframe = null;
          state.selectedKeyframes = [];
        });
      },
      
      // =========================================================================
      // FOCUS-AWARE DELETION
      // =========================================================================
      
      handleDelete: () => {
        const { focusedPane, selectedKeyframes, selection, scene } = get();
        
        // If timeline is focused and keyframes are selected, delete them all (one undo step)
        if (focusedPane === 'timeline' && selectedKeyframes.length > 0) {
          const exists = selectedKeyframes.some(({ trackId, keyframeId }) =>
            get().keyframes.get(trackId)?.some((kf) => kf.id === keyframeId));
          if (exists) {
            get().deleteKeyframes(selectedKeyframes);
            get().clearKeyframeSelection();
            return true;
          }
        }
        
        // Fall back to deleting selected item
        if (selection.ids.length > 0 && selection.type && selection.type !== 'scene') {
          get().deleteSelected();
          return true;
        }
        
        return false;
      },

      // =========================================================================
      // EDITOR STATE SYNC (LOCAL-FIRST WITH INDEXEDDB)
      // =========================================================================

      getEditorStateData: (): EditorState => {
        const {
          selection, playheadPosition, viewportZoom, viewportPan, previewOrientation,
          timelineHeight, timelineZoom, timelineScrollLeft, timelineScrollTop,
          expandedTracks, collapsedGroups, selectedKeyframe, focusedPane
        } = get();
        
        return {
          // Selection
          selectedElementId: selection.type !== 'scene' && selection.type !== 'signal' && selection.ids.length > 0 
            ? selection.ids[0] 
            : undefined,
          selectedElementType: selection.type !== 'scene' && selection.type !== 'signal' 
            ? selection.type as 'sprite' | 'dialog' | 'sound' | 'background' 
            : undefined,
          
          // Playback
          playheadPosition,
          timelineZoom,
          
          // Timeline state
          timelineHeight,
          timelineScrollLeft,
          timelineScrollTop,
          expandedTracks: Array.from(expandedTracks),
          collapsedGroups: Array.from(collapsedGroups),
          selectedKeyframe,
          
          // Viewport
          viewportZoom,
          viewportPan,
          previewOrientation,
          
          // Focus
          focusedPane,
        };
      },

      syncEditorStateToServer: async () => {
        const { scene, getEditorStateData } = get();
        if (!scene) return;
        const editorStateData = getEditorStateData();
        const json = serialiseEditorState(editorStateData);
        if (json === serverEditorStateJson) return;

        const timestamp = Date.now();
        const generation = loadGeneration;
        try {
          const response = await apiClient.updateScene(scene.id, { editorState: { ...editorStateData, timestamp } });
          if (response.success && generation === loadGeneration) {
            serverEditorStateJson = json;
            set((state) => {
              state.editorStateTimestamp = timestamp;
            });
          }
        } catch (error) {
          // UI state is a convenience: IndexedDB still holds it.
          console.warn('[EditorState] Failed to write to the server:', error);
        }
      },

      // =========================================================================
      // UTILITY
      // =========================================================================

      // =========================================================================
      // STORYBOARD MODE OPERATIONS
      // =========================================================================

      // =========================================================================
      // CHOICE OPERATIONS
      // =========================================================================

      // Spec 008: 0–4 options; an option's id is its routing key and never
      // changes; removing the last option leaves `[]`, which the save sends.
      addChoice: () => {
        set((state) => {
          if (!state.scene) return;
          if (!state.scene.choices) state.scene.choices = [];
          if (state.scene.choices.length >= MAX_CHOICES) return;
          state.scene.choices.push({ id: generateElementId('choice'), text: '' });
          if (!state.scene.choiceSettings) state.scene.choiceSettings = {};
          state.isDirty = true;
        });
      },

      updateChoice: (id: string, updates: Partial<ChoiceOption>) => {
        set((state) => {
          if (!state.scene) return;
          const choice = state.scene.choices?.find((c) => c.id === id);
          if (!choice) return;
          const { id: _ignoredId, ...patch } = updates;
          Object.assign(choice, patch);
          state.isDirty = true;
        });
      },

      removeChoice: (id: string) => {
        set((state) => {
          if (!state.scene) return;
          if (!state.scene.choices) return;
          if (!state.scene.choices.some((c) => c.id === id)) return;
          state.scene.choices = state.scene.choices.filter((c) => c.id !== id);
          state.isDirty = true;
        });
      },

      moveChoice: (id: string, toIndex: number) => {
        const choices = get().scene?.choices ?? [];
        const from = choices.findIndex((c) => c.id === id);
        if (from < 0 || !Number.isInteger(toIndex) || toIndex < 0 || toIndex >= choices.length || toIndex === from) return;
        set((state) => {
          const list = state.scene?.choices;
          if (!list) return;
          const [moved] = list.splice(from, 1);
          list.splice(toIndex, 0, moved);
          state.isDirty = true;
        });
      },

      clearChoices: () => {
        set((state) => {
          if (!state.scene) return;
          state.scene.choices = [];
          state.isDirty = true;
        });
      },

      setChoiceSettings: (settings: Partial<ChoiceSettings>) => {
        set((state) => {
          if (!state.scene) return;
          if (!state.scene.choiceSettings) state.scene.choiceSettings = {};
          Object.assign(state.scene.choiceSettings, settings);
          state.isDirty = true;
        });
      },

      applyOperations: (steps, options) => {
        const { scene, keyframes } = get();
        if (!scene) {
          return { ok: false, failedIndex: 0, errors: [{ code: 'not_found', message: 'no scene is loaded' }] };
        }
        // The document the operations see carries the store's keyframes (spec 021 D-1).
        const tracksIn = keyframesToArray(keyframes);
        const docIn = { ...scene, keyframes: tracksIn } as SceneDocument;
        const result = replaySteps(docIn, steps, undefined, options?.naturalSizes, options?.mediaLengths);
        if (result.ok === false) return result;
        if (result.doc === docIn) return { ok: true };
        const { keyframes: tracksOut = [], ...sceneOut } = result.doc;
        const keys = new Set([...Object.keys(scene), ...Object.keys(sceneOut)]);
        keys.delete('keyframes');
        const sceneChanged = [...keys].some((k) => (sceneOut as Record<string, unknown>)[k] !== (docIn as unknown as Record<string, unknown>)[k]);
        const keyframesChanged = tracksOut !== tracksIn;
        if (!sceneChanged && !keyframesChanged) return { ok: true };
        const nextScene = sceneChanged
          ? ({ ...sceneOut, ...('keyframes' in scene ? { keyframes: scene.keyframes } : {}) } as Story)
          : undefined;
        const nextKeyframes = keyframesChanged
          ? new Map(tracksOut.filter((t) => t.keyframes.length > 0).map((t) => [t.trackId, t.keyframes] as const))
          : undefined;
        asOneStep(() => {
          // Scene and keyframes in one update: one undo step over both (018's snapshot holds both).
          set((state) => {
            if (nextScene) state.scene = nextScene;
            if (nextKeyframes) state.keyframes = nextKeyframes;
            state.isDirty = true;
          });
          // Only ids the batch removed leave the selection (as after undo).
          const st = get();
          const selection = pruneSelection(st.scene, st.selection);
          if (selection !== st.selection) set((state) => { state.selection = selection; });
        });
        return { ok: true };
      },

      fillPicture: async (slotId, mediaId, opts = {}) => {
        const scene = get().scene;
        if (!scene) return { ok: false, reason: 'failed', errors: [{ code: 'not_found', message: 'no scene is loaded' }] };
        const slot = scenePictureMapOf(scene as SceneDocument).slots.find((x) => x.id === slotId);
        const { loadPictureSize } = await import('./assistantStore');
        const size = await loadPictureSize(mediaId);
        const args: Record<string, unknown> = { slot: slotId, mediaId };
        if (size && size.width > 0 && size.height > 0) args.size = { width: size.width, height: size.height };
        // A use as the step's schema takes it: the picture a place shows (`mediaId`) is read-only.
        if (opts.uses) args.uses = opts.uses.map(({ mediaId: _shown, ...use }) => use);
        const result = get().applyOperations([{ name: 'fillPictureSlot', args, createdIds: [] }]);
        if (result.ok === false) return { ok: false, reason: 'failed', errors: result.errors };
        await get().saveScene();
        const places = slot
          ? slot.uses.filter((u) => u.kind !== 'line' && (!opts.uses || opts.uses.some((x) => sameUse(x, u)))).length
          : 0;
        return { ok: true, places, label: slot ? pictureSlotLabel(slot) : slotId };
      },

      discardPictures: async (mediaIds) => {
        const result = get().applyOperations([{ name: 'clearPictures', args: { mediaIds: [...new Set(mediaIds)] }, createdIds: [] }]);
        if (result.ok === false) return { ok: false, reason: 'failed', errors: result.errors };
        await get().saveScene();
        return { ok: true };
      },

      markDirty: () => {
        set((state) => {
          state.isDirty = true;
        });
      },

      markClean: () => {
        set((state) => {
          state.isDirty = false;
          state.saveStatus = 'saved';
          state.saveError = null;
        });
      },
    })),
    {
      // Zundo options for undo/redo (spec 018)
      limit: UNDO_LIMIT,
      // The document only: scene + authored keyframes, never UI state
      partialize: (state): HistorySnapshot => ({
        scene: state.scene,
        keyframes: state.keyframes,
      }),
      // A UI-state update leaves both references untouched: no entry
      equality: (pastState, currentState) => sameDocument(pastState, currentState),
    }
  )
  )  // Close subscribeWithSelector
);

// =============================================================================
// EDITOR UI STATE → INDEXEDDB (spec 018)
// Every UI-state change is written to IndexedDB 1 s after the last one (an
// orientation switch at once, in setPreviewOrientation). Nothing here talks to
// the server: the server copy goes inside saveScene and once on leaving.
// =============================================================================

const editorStateSelector = (state: SceneState) => ({
  scene: state.scene?.id,
  selection: state.selection,
  playheadPosition: state.playheadPosition,
  viewportZoom: state.viewportZoom,
  viewportPan: state.viewportPan,
  previewOrientation: state.previewOrientation,
  timelineHeight: state.timelineHeight,
  timelineZoom: state.timelineZoom,
  timelineScrollLeft: state.timelineScrollLeft,
  timelineScrollTop: state.timelineScrollTop,
  expandedTracks: Array.from(state.expandedTracks),
  collapsedGroups: Array.from(state.collapsedGroups),
  selectedKeyframe: state.selectedKeyframe,
  focusedPane: state.focusedPane,
});

useSceneStore.subscribe(
  editorStateSelector,
  () => {
    const state = useSceneStore.getState();
    const sceneId = state.scene?.id;
    if (!sceneId || state.isLoading) return;
    debouncedLocalSave(sceneId, state.getEditorStateData());
  },
  {
    equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  }
);

// =============================================================================
// DOCUMENT REVISIONS + AUTOSAVE (spec 004)
// Every change to the loaded scene's document while it is dirty bumps the
// revision and (re)schedules autosave. Loading and saving do not count.
// =============================================================================

useSceneStore.subscribe((state, prev) => {
  if (!state.scene || !prev.scene || state.scene.id !== prev.scene.id) return;
  if (!state.isDirty) return;
  const docChanged = state.scene !== prev.scene || state.keyframes !== prev.keyframes;
  const becameDirty = !prev.isDirty;
  if (!docChanged && !becameDirty) return;
  docRevision++;
  if (state.saveStatus === 'saved') useSceneStore.setState({ saveStatus: 'dirty' });
  scheduleAutosave();
});

// =============================================================================
// UNDO/REDO HOOKS
// =============================================================================

/** Undo the last document step (marks dirty, prunes the selection). */
export const useSceneUndo = () => useSceneStore((s) => s.undo);
/** Redo the last undone document step. */
export const useSceneRedo = () => useSceneStore((s) => s.redo);
/** Subscribed to the undo history: re-renders only when availability changes. */
export const useCanUndo = () => useStore(useSceneStore.temporal, (t) => t.pastStates.length > 0);
/** Subscribed to the undo history: re-renders only when availability changes. */
export const useCanRedo = () => useStore(useSceneStore.temporal, (t) => t.futureStates.length > 0);

/**
 * After undo/redo: the scene is dirty (autosave saves it), the selection is
 * pruned to elements that exist. None of this is a step of its own.
 */
function afterHistoryMove() {
  const st = useSceneStore.getState();
  const selection = pruneSelection(st.scene, st.selection);
  const kfExists = (k: { trackId: string; keyframeId: string }) =>
    Boolean(st.keyframes.get(k.trackId)?.some((kf) => kf.id === k.keyframeId));
  const selectedKeyframes = st.selectedKeyframes.filter(kfExists);
  withoutHistory(() => {
    useSceneStore.setState({
      selection,
      selectedKeyframes: selectedKeyframes.length === st.selectedKeyframes.length ? st.selectedKeyframes : selectedKeyframes,
      selectedKeyframe: st.selectedKeyframe && kfExists(st.selectedKeyframe) ? st.selectedKeyframe : null,
      isDirty: true,
    });
  });
}

// =============================================================================
// DRAFT CACHE HELPERS
// =============================================================================

/**
 * Cache the current dirty state so it survives an unmount/remount cycle (in
 * memory only, never IndexedDB). Returns true when a new draft was written;
 * an unchanged scene that is already cached is not re-cached.
 */
export function cacheDraft(): boolean {
  const s = useSceneStore.getState();
  if (!s.scene || !s.isDirty) return false;
  const existing = _draftCache.get(s.scene.id);
  if (
    existing &&
    (existing.sourceScene === s.scene || existing.scene === s.scene) &&
    (existing.sourceKeyframes === s.keyframes || existing.keyframes === s.keyframes)
  ) {
    return false;
  }
  _draftCache.set(s.scene.id, {
    sourceScene: s.scene,
    sourceKeyframes: s.keyframes,
    scene: JSON.parse(JSON.stringify(s.scene)),
    keyframes: new Map(Array.from(s.keyframes.entries()).map(([k, v]) => [k, JSON.parse(JSON.stringify(v))])),
    selection: { ...s.selection },
    selectedKeyframe: s.selectedKeyframe ? { ...s.selectedKeyframe } : null,
    playheadPosition: s.playheadPosition,
    previewOrientation: s.previewOrientation,
    viewportZoom: s.viewportZoom,
    viewportPan: { ...s.viewportPan },
    timelineHeight: s.timelineHeight,
    timelineZoom: s.timelineZoom,
    timelineScrollLeft: s.timelineScrollLeft,
    timelineScrollTop: s.timelineScrollTop,
    expandedTracks: new Set(s.expandedTracks),
    collapsedGroups: new Set(s.collapsedGroups),
    focusedPane: s.focusedPane,
  });
  return true;
}

/** Remove a cached draft (e.g. after save or explicit discard). */
export function clearDraft(sceneId: number): void {
  _draftCache.delete(sceneId);
}

/**
 * The scene as a preview plays it (spec 009): the open editor's in-memory
 * scene when it is `sceneId`, else its cached draft, else `null` (the caller
 * loads the saved scene). Keyframes are the authored ones, as saved.
 */
export function previewSceneInput(sceneId: number): (Story & { keyframes: KeyframeTrack[] }) | null {
  const s = useSceneStore.getState();
  if (s.scene && Number(s.scene.id) === sceneId && !s.isLoading) {
    return { ...s.scene, keyframes: keyframesToArray(s.keyframes) };
  }
  const draft = _draftCache.get(sceneId);
  if (draft) return { ...draft.scene, keyframes: keyframesToArray(draft.keyframes) };
  return null;
}

/** The scenes with a cached draft (spec 009: the pop-out preview plays them). */
export function draftSceneIds(): number[] {
  return Array.from(_draftCache.keys());
}

/** Check whether a draft exists for the given scene. */
export function hasDraft(sceneId: number): boolean {
  return _draftCache.has(sceneId);
}

// =============================================================================
// SIGN-OUT RESET (spec 001)
// =============================================================================

/**
 * Back to the initial state with nothing pending: debounced saves cancelled,
 * draft cache and undo history cleared. Runs on every sign-out so the next
 * user (or the same user after a forced sign-out) starts clean.
 */
export function resetSceneStore(): void {
  clearAutosaveTimers();
  loadGeneration++;
  docRevision = 0;
  saveInFlight = null;
  queuedJob = null;
  queuedPromise = null;
  if (localSaveTimeout) clearTimeout(localSaveTimeout);
  localSaveTimeout = null;
  serverEditorStateJson = null;
  _draftCache.clear();
  useSceneStore.setState({
    ...initialState,
    selection: { ...initialState.selection },
    keyframes: new Map(),
    selectedKeyframes: [],
    viewportPan: { ...initialState.viewportPan },
    expandedTracks: new Set(),
    collapsedGroups: new Set(),
  });
  resetHistory();
}

registerSessionReset(resetSceneStore);
